import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { loadRazorpay } from "../../../Utils/razorpay";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FiHome,
  FiUser,
  FiMapPin,
  FiArrowLeft,
  FiPlus,
  FiCheck,
  FiPhone,
  FiCheckCircle
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const OrderSuccessModal = ({ order, onClose, onGoToOrders }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-xl max-w-md w-full px-6 py-8 text-center">
      <div className="flex flex-col items-center">
        <FiCheckCircle className="text-green-500 text-5xl mb-3" />
        <h2 className="text-2xl font-bold text-green-600 mb-2">Order Placed Successfully!</h2>
        <p className="text-gray-700 mb-2">Thank you for shopping with us. Your order has been placed and is being processed.</p>
        <div className="bg-gray-50 p-4 rounded-lg mb-4 w-full text-left mt-4">
          <h3 className="font-semibold mb-1">Order Details:</h3>
          <div className="text-sm text-gray-700">
            <div><span className="font-medium">Order ID:</span> {order?._id || "N/A"}</div>
            <div><span className="font-medium">Amount Paid:</span> ₹{order?.totalAmount?.toFixed(2) || "N/A"}</div>
            <div><span className="font-medium">Payment Mode:</span> {order?.paymentMethod?.type === "cod" ? "Cash on Delivery" : "Online"}</div>
            <div><span className="font-medium">Order Status:</span> {order?.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "Pending"}</div>
            {order?.shippingAddress && (
              <div className="mt-2">
                <span className="font-medium">Shipping To:</span> <br />
                <span className="text-gray-600">{[
                  order.shippingAddress.fullName,
                  order.shippingAddress.phone,
                  order.shippingAddress.addressLine1,
                  order.shippingAddress.addressLine2,
                  `${order.shippingAddress.city}, ${order.shippingAddress.state} ${order.shippingAddress.zipCode}`,
                  order.shippingAddress.country
                ].filter(Boolean).join(", ")}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-center gap-3 mt-5">
        <button
          className="px-4 py-2 min-w-[120px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
          onClick={onGoToOrders}
        >
          View Orders
        </button>
        <button
          className="px-4 py-2 min-w-[120px] rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const ShippingAddress = ({
  onClose,
  onSubmit,
  orderSummary,
  cartItems,
  clearCart,
  fetchCart
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "India",
    isDefault: false
  });
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amount, setAmount] = useState(0);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
  const [checkoutSummary, setCheckoutSummary] = useState(null);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

  const [orderSuccess, setOrderSuccess] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  const { user } = useSelector(state => state.auth);
  const navigate = useNavigate();

  const fetchAddresses = async () => {
    const token = Cookies.get("ecom_user_token");
    setIsLoadingAddresses(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const receivedAddresses = response.data?.shippingAddresses || [];
      const sortedAddresses = [...receivedAddresses].sort((a, b) => {
        if (a.isDefault !== b.isDefault) return b.isDefault ? 1 : -1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setAddresses(sortedAddresses);
      const defaultAddress = sortedAddresses.find(addr => addr.isDefault) || sortedAddresses[0];
      if (defaultAddress) setSelectedAddressId(defaultAddress._id);
    } catch (err) {
      console.error("Error fetching addresses:", err);
      toast.error(err.response?.data?.msg || "Failed to load addresses");
      setAddresses([]);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const fetchCheckoutSummary = async (paymentMethodType = paymentMethod) => {
    if (!selectedAddressId) {
      setError("Please select a shipping address");
      return;
    }
    
    setIsLoadingCheckout(true);
    setError("");
    const token = Cookies.get("ecom_user_token");
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/checkout-summary`,
        {
          shippingAddressId: selectedAddressId,
          paymentMethod: {
            type: paymentMethodType === "razorpay" ? "prepaid" : paymentMethodType
          }
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.data.success) {
        setCheckoutSummary(response.data);
      } else {
        throw new Error(response.data.message || "Failed to fetch checkout summary");
      }
    } catch (err) {
      console.error("Error fetching checkout summary:", err);
      const errorMsg = err.response?.data?.message || err.response?.data?.message || "Failed to load checkout details";
      setError(errorMsg);
      toast.error(errorMsg);
      setCheckoutSummary(null);
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  useEffect(() => {
    if (step === 1) {
      fetchAddresses();
    }
    // eslint-disable-next-line
  }, [step]);

  useEffect(() => {
    if (step === 2 && selectedAddressId) {
      fetchCheckoutSummary();
    }
    // eslint-disable-next-line
  }, [step, selectedAddressId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev, [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    const token = Cookies.get("ecom_user_token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-shipping-address`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      if (response.status === 201) {
        const updatedAddresses = response.data.shippingAddresses || [];
        if (!Array.isArray(updatedAddresses)) {
          throw new Error("Invalid address data received");
        }
        const newAddress = updatedAddresses.reduce((latest, current) => {
          if (!latest) return current;
          return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
        }, null);
        if (!newAddress || !newAddress._id) throw new Error("New address not found in response");
        setAddresses(updatedAddresses);
        setSelectedAddressId(newAddress._id);
        setShowAddressForm(false);
        setFormData({
          fullName: "",
          phone: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          zipCode: "",
          country: "India",
          isDefault: false
        });
        toast.success(response.data.msg || "Address saved successfully!");
      }
    } catch (err) {
      console.error("Address save error:", { error: err, response: err.response?.data });
      const errorMsg = err.response?.data?.msg ||
        err.response?.data?.message || err.message || "Could not save address. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    
    if (!selectedAddressId) {
      setError("Please select a shipping address");
      toast.error("Please select a shipping address");
      setIsSubmitting(false);
      return;
    }
    
    // Don't proceed if there's an error or checkout summary is still loading
    if (error || isLoadingCheckout || !checkoutSummary) {
      setIsSubmitting(false);
      return;
    }
    
    try {
      if (paymentMethod === "cod") {
        await handlePlaceOrder();
      } else {
        await handleRazorpayPayment();
      }
    } catch (err) {
      console.error("Payment error:", err);
      const errorMsg = err.message || "An error occurred during payment processing";
      setError(errorMsg);
      toast.error(errorMsg);
      setIsSubmitting(false);
    }
  };

  const handlePlaceOrder = async () => {
    const token = Cookies.get("ecom_user_token");
    try {
      const requestBody = {
        shippingAddressId: selectedAddressId,
        paymentMethod: { type: paymentMethod },
        shippingCost: checkoutSummary?.summary?.shipping || orderSummary.shippingCharges
      };
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      if (response.status === 201) {
        setAmount(response?.data?.razorpayOrder?.amount);
        setOrderSuccess(true);
        setPlacedOrder(response.data.order);
        clearCart();
        fetchCart();
      } else {
        throw new Error(response.data.message || "Failed to place order");
      }
    } catch (err) {
      toast.error(err.message || "Failed to place order");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRazorpayPayment = async () => {
    const token = Cookies.get("ecom_user_token");
    const currency = "INR";
    try {
      const requestBody = {
        shippingAddressId: selectedAddressId,
        paymentMethod: { type: "razorpay" },
        shippingCost: checkoutSummary?.summary?.shipping || orderSummary.shippingCharges
      };
      const orderResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      const orderId = orderResponse?.data?.order?._id;
      const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
      setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);
      await loadRazorpay();
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_heVUS0edM8228s",
        amount: orderResponse?.data?.razorpayOrder?.amount,
        currency,
        name: "Uresia",
        description: "Order Payment",
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            const verificationResponse = await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify-payment`,
              {
                orderId,
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json"
                }
              }
            );
            if (verificationResponse?.status === 200) {
              setOrderSuccess(true);
              setPlacedOrder(verificationResponse.data.order);
              clearCart();
              fetchCart();
            } else {
              const errorMsg = verificationResponse.data.message || "Payment verification failed";
              setError(errorMsg);
              toast.error(errorMsg);
              fetchCart();
            }
          } catch (err) {
            console.error("Error verifying payment:", err);
            const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
            setError(errorMsg);
            toast.error(errorMsg);
            fetchCart();
          } finally {
            setIsSubmitting(false);
          }
        },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "9999999999"
        },
        theme: { color: "#3399cc" },
        modal: {
          ondismiss: function () {
            toast.info("Payment window closed");
            fetchCart();
            setIsSubmitting(false);
          }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        fetchCart();
        setIsSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      console.error("Error in Razorpay payment:", err);
      const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
      setError(errorMsg);
      toast.error(errorMsg);
      fetchCart();
      setIsSubmitting(false);
    }
  };

  const formatAddress = (address) => {
    if (!address) return "";
    return [
      address.fullName,
      address.phone,
      address.addressLine1,
      address.addressLine2,
      `${address.city}, ${address.state} ${address.zipCode}`,
      address.country
    ].filter(Boolean).join(", ");
  };

  const handleBackToAddress = () => {
    setStep(1);
    setShowAddressForm(false);
    setCheckoutSummary(null);
    if (step === 2) {
      setFormData({
        fullName: "",
        phone: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        country: "India",
        isDefault: false
      });
    }
  };

  const handleGoToOrders = () => {
    setOrderSuccess(false);
    onClose();
    navigate("/profile/orders");
  };

  const handleSuccessClose = () => {
    setOrderSuccess(false);
    onClose();
  };

  const handlePaymentMethodChange = async (method) => {
    setPaymentMethod(method);
    // Refetch checkout summary when payment method changes
    if (selectedAddressId) {
      await fetchCheckoutSummary(method);
    }
  };

  // Determine if the Complete Order button should be disabled
  const isCompleteOrderDisabled = isSubmitting || isLoadingCheckout || error || !checkoutSummary;

  return (
    <>
      {orderSuccess && placedOrder && (
        <OrderSuccessModal
          order={placedOrder}
          onClose={handleSuccessClose}
          onGoToOrders={handleGoToOrders}
        />
      )}
      {!orderSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
              step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
            }`}>
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  {step === 1 ? (
                    showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
                  ) : (
                    <><FiUser /> Payment Method</>
                  )}
                </h2>
                <button
                  onClick={onClose}
                  className="text-white hover:text-gray-200 text-2xl"
                  type="button"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[80vh]">
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
                  {error}
                </div>
              )}
              {step === 1 ? (
                showAddressForm ? (
                  <form onSubmit={handleAddressSubmit}>
                    <div className="space-y-4">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                          Full Name*
                        </label>
                        <div className="relative">
                          <FiUser className="absolute left-3 top-3 text-gray-400" />
                          <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="John Doe"
                          />
                        </div>
                      </div>
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                          Phone Number*
                        </label>
                        <div className="relative">
                          <FiPhone className="absolute left-3 top-3 text-gray-400" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Phone number"
                            pattern="[0-9]{10}"
                            maxLength="10"
                          />
                        </div>
                      </div>
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                          Address Line 1*
                        </label>
                        <div className="relative">
                          <FiMapPin className="absolute left-3 top-3 text-gray-400" />
                          <input
                            type="text"
                            name="addressLine1"
                            value={formData.addressLine1}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Street address, P.O. box"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                          Address Line 2
                        </label>
                        <input
                          type="text"
                          name="addressLine2"
                          value={formData.addressLine2}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Apartment, suite, unit, building"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                            City*
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="City"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                            State*
                          </label>
                          <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="State"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                            ZIP Code*
                          </label>
                          <input
                            type="text"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Postal code"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
                            Country*
                          </label>
                          <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Country"
                          />
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isDefault"
                          name="isDefault"
                          checked={formData.isDefault}
                          onChange={handleChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
                          Set as default shipping address
                        </label>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowAddressForm(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
                        disabled={isSubmitting}
                      >
                        <FiArrowLeft className="mr-1" /> Back
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Saving..." : "Save Address"}
                      </button>
                    </div>
                    </form>
                ) : (
                  <div>
                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <FiMapPin /> Select Shipping Address
                    </h3>
                    {isLoadingAddresses ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : addresses.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
                        <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
                        <p className="text-gray-600 mb-4">No saved addresses found</p>
                        <button
                          onClick={() => setShowAddressForm(true)}
                          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
                        >
                          <FiPlus className="mr-1" /> Add New Address
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
                          <div className="grid grid-cols-1 gap-3">
                            {addresses.map(address => (
                              <div
                                key={address._id}
                                className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
                                  selectedAddressId === address._id
                                    ? 'border-blue-500 border-2 bg-blue-50 shadow-sm'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                onClick={() => setSelectedAddressId(address._id)}
                              >
                                <div className="flex">
                                  <div className={`mr-3 mt-1 flex-shrink-0 ${
                                    selectedAddressId === address._id
                                      ? 'text-blue-500'
                                      : 'text-gray-400'
                                  }`}>
                                    {selectedAddressId === address._id ? (
                                      <FiCheck className="text-xl" />
                                    ) : (
                                      <div className="w-5 h-5 rounded-full border border-gray-300"></div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    {address.fullName && (
                                      <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
                                    )}
                                    <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
                                      {formatAddress(address)}
                                    </p>
                                    {address.isDefault && (
                                      <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={() => setShowAddressForm(true)}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
                          >
                            <FiPlus /> Add New Address
                          </button>
                          <button
                            onClick={() => setStep(2)}
                            className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
                              selectedAddressId
                                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            disabled={!selectedAddressId}
                          >
                            Continue to Payment <FiArrowLeft className="transform rotate-180" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )
              ) : (
                <form onSubmit={handlePaymentSubmit}>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                        <FiUser /> Select Payment Method
                      </h3>
                      <div className="space-y-3">
                        <label className={`flex items-center p-4 border rounded-xl transition-all ${
                          paymentMethod === "cod"
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="cod"
                            checked={paymentMethod === "cod"}
                            onChange={() => handlePaymentMethodChange("cod")}
                            className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="ml-3">
                            <span className="block font-medium">Cash on Delivery</span>
                            <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
                          </div>
                        </label>
                        <label className={`flex items-center p-4 border rounded-xl transition-all ${
                          paymentMethod === "razorpay"
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="razorpay"
                            checked={paymentMethod === "razorpay"}
                            onChange={() => handlePaymentMethodChange("razorpay")}
                            className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="ml-3">
                            <span className="block font-medium">Pay with Razorpay</span>
                            <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {isLoadingCheckout ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : checkoutSummary && !error ? (
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="text-lg font-medium mb-3">Order Summary</h3>
                        <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Subtotal:</span>
                            <span className="font-medium">₹{checkoutSummary.summary.subtotal?.toFixed(2)}</span>
                          </div>
                          {checkoutSummary.summary.taxes > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Taxes:</span>
                              <span className="font-medium">₹{checkoutSummary.summary.taxes?.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">Shipping:</span>
                            <span className="font-medium">₹{checkoutSummary.summary.shipping?.toFixed(2)}</span>
                          </div>
                          {checkoutSummary.summary.codCharge > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">COD Charges:</span>
                              <span className="font-medium">₹{checkoutSummary.summary.codCharge?.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
                            <span className="text-gray-800">Total:</span>
                            <span className="text-lg text-blue-600">₹{checkoutSummary.summary.totalAmount?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
                    <button
                      type="button"
                      onClick={handleBackToAddress}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
                      disabled={isSubmitting}
                    >
                      <FiArrowLeft /> Back to Address
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 text-white rounded-lg text-sm font-medium transition-opacity flex items-center justify-center gap-2 ${
                        isCompleteOrderDisabled 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:opacity-90'
                      }`}
                      disabled={isCompleteOrderDisabled}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                          Processing...
                        </>
                      ) : isLoadingCheckout ? (
                        <>
                          <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                          Loading...
                        </>
                      ) : (
                        "Complete Order"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import {
//   FiHome,
//   FiUser,
//   FiMapPin,
//   FiArrowLeft,
//   FiPlus,
//   FiCheck,
//   FiPhone,
//   FiCheckCircle
// } from "react-icons/fi";
// import { useNavigate } from "react-router-dom";

// const OrderSuccessModal = ({ order, onClose, onGoToOrders }) => (
//   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//     <div className="bg-white rounded-xl shadow-xl max-w-md w-full px-6 py-8 text-center">
//       <div className="flex flex-col items-center">
//         <FiCheckCircle className="text-green-500 text-5xl mb-3" />
//         <h2 className="text-2xl font-bold text-green-600 mb-2">Order Placed Successfully!</h2>
//         <p className="text-gray-700 mb-2">Thank you for shopping with us. Your order has been placed and is being processed.</p>
//         <div className="bg-gray-50 p-4 rounded-lg mb-4 w-full text-left mt-4">
//           <h3 className="font-semibold mb-1">Order Details:</h3>
//           <div className="text-sm text-gray-700">
//             <div><span className="font-medium">Order ID:</span> {order?._id || "N/A"}</div>
//             <div><span className="font-medium">Amount Paid:</span> ₹{order?.totalAmount?.toFixed(2) || "N/A"}</div>
//             <div><span className="font-medium">Payment Mode:</span> {order?.paymentMethod?.type === "cod" ? "Cash on Delivery" : "Online"}</div>
//             <div><span className="font-medium">Order Status:</span> {order?.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "Pending"}</div>
//             {order?.shippingAddress && (
//               <div className="mt-2">
//                 <span className="font-medium">Shipping To:</span> <br />
//                 <span className="text-gray-600">{[
//                   order.shippingAddress.fullName,
//                   order.shippingAddress.phone,
//                   order.shippingAddress.addressLine1,
//                   order.shippingAddress.addressLine2,
//                   `${order.shippingAddress.city}, ${order.shippingAddress.state} ${order.shippingAddress.zipCode}`,
//                   order.shippingAddress.country
//                 ].filter(Boolean).join(", ")}</span>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//       <div className="flex justify-center gap-3 mt-5">
//         <button
//           className="px-4 py-2 min-w-[120px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
//           onClick={onGoToOrders}
//         >
//           View Orders
//         </button>
//         <button
//           className="px-4 py-2 min-w-[120px] rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition"
//           onClick={onClose}
//         >
//           Close
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const ShippingAddress = ({
//   onClose,
//   onSubmit,
//   orderSummary,
//   cartItems,
//   clearCart,
//   fetchCart
// }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     phone: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
//   const [checkoutSummary, setCheckoutSummary] = useState(null);
//   const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

//   const [orderSuccess, setOrderSuccess] = useState(false);
//   const [placedOrder, setPlacedOrder] = useState(null);

//   const { user } = useSelector(state => state.auth);
//   const navigate = useNavigate();

//   const fetchAddresses = async () => {
//     const token = Cookies.get("ecom_user_token");
//     setIsLoadingAddresses(true);
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );
//       const receivedAddresses = response.data?.shippingAddresses || [];
//       const sortedAddresses = [...receivedAddresses].sort((a, b) => {
//         if (a.isDefault !== b.isDefault) return b.isDefault ? 1 : -1;
//         return new Date(b.createdAt) - new Date(a.createdAt);
//       });
//       setAddresses(sortedAddresses);
//       const defaultAddress = sortedAddresses.find(addr => addr.isDefault) || sortedAddresses[0];
//       if (defaultAddress) setSelectedAddressId(defaultAddress._id);
//     } catch (err) {
//       console.error("Error fetching addresses:", err);
//       toast.error(err.response?.data?.msg || "Failed to load addresses");
//       setAddresses([]);
//     } finally {
//       setIsLoadingAddresses(false);
//     }
//   };

//   const fetchCheckoutSummary = async (paymentMethodType = paymentMethod) => {
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       return;
//     }
    
//     setIsLoadingCheckout(true);
//     setError("");
//     const token = Cookies.get("ecom_user_token");
    
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/checkout-summary`,
//         {
//           shippingAddressId: selectedAddressId,
//           paymentMethod: {
//             type: paymentMethodType === "razorpay" ? "prepaid" : paymentMethodType
//           }
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
      
//       if (response.data.success) {
//         setCheckoutSummary(response.data);
//       } else {
//         throw new Error(response.data.message || "Failed to fetch checkout summary");
//       }
//     } catch (err) {
//       console.error("Error fetching checkout summary:", err);
//       const errorMsg = err.response?.data?.message || err.response?.data?.message || "Failed to load checkout details";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsLoadingCheckout(false);
//     }
//   };

//   useEffect(() => {
//     if (step === 1) {
//       fetchAddresses();
//     }
//     // eslint-disable-next-line
//   }, [step]);

//   useEffect(() => {
//     if (step === 2 && selectedAddressId) {
//       fetchCheckoutSummary();
//     }
//     // eslint-disable-next-line
//   }, [step, selectedAddressId]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev, [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-shipping-address`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       if (response.status === 201) {
//         const updatedAddresses = response.data.shippingAddresses || [];
//         if (!Array.isArray(updatedAddresses)) {
//           throw new Error("Invalid address data received");
//         }
//         const newAddress = updatedAddresses.reduce((latest, current) => {
//           if (!latest) return current;
//           return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
//         }, null);
//         if (!newAddress || !newAddress._id) throw new Error("New address not found in response");
//         setAddresses(updatedAddresses);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
//         setFormData({
//           fullName: "",
//           phone: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });
//         toast.success(response.data.msg || "Address saved successfully!");
//       }
//     } catch (err) {
//       console.error("Address save error:", { error: err, response: err.response?.data });
//       const errorMsg = err.response?.data?.msg ||
//         err.response?.data?.message || err.message || "Could not save address. Please try again.";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: { type: paymentMethod },
//         shippingCost: checkoutSummary?.summary?.shipping || orderSummary.shippingCharges
//       };
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         setOrderSuccess(true);
//         setPlacedOrder(response.data.order);
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: { type: "razorpay" },
//         shippingCost: checkoutSummary?.summary?.shipping || orderSummary.shippingCharges
//       };
//       const orderResponse = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);
//       await loadRazorpay();
//       const options = {
//         key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function (response) {
//           try {
//             const verificationResponse = await axios.post(
//               `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify-payment`,
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );
//             if (verificationResponse?.status === 200) {
//               setOrderSuccess(true);
//               setPlacedOrder(verificationResponse.data.order);
//               clearCart();
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: { color: "#3399cc" },
//         modal: {
//           ondismiss: function () {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };
//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function (response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     if (!address) return "";
//     return [
//       address.fullName,
//       address.phone,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   const handleBackToAddress = () => {
//     setStep(1);
//     setShowAddressForm(false);
//     setCheckoutSummary(null);
//     if (step === 2) {
//       setFormData({
//         fullName: "",
//         phone: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
//     }
//   };

//   const handleGoToOrders = () => {
//     setOrderSuccess(false);
//     onClose();
//     navigate("/profile/orders");
//   };

//   const handleSuccessClose = () => {
//     setOrderSuccess(false);
//     onClose();
//   };

//   const handlePaymentMethodChange = async (method) => {
//     setPaymentMethod(method);
//     // Refetch checkout summary when payment method changes
//     if (selectedAddressId) {
//       await fetchCheckoutSummary(method);
//     }
//   };

//   return (
//     <>
//       {orderSuccess && placedOrder && (
//         <OrderSuccessModal
//           order={placedOrder}
//           onClose={handleSuccessClose}
//           onGoToOrders={handleGoToOrders}
//         />
//       )}
//       {!orderSuccess && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//               step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//             }`}>
//             <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//               <div className="flex justify-between items-center">
//                 <h2 className="text-xl font-bold flex items-center gap-2">
//                   {step === 1 ? (
//                     showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//                   ) : (
//                     <><FiUser /> Payment Method</>
//                   )}
//                 </h2>
//                 <button
//                   onClick={onClose}
//                   className="text-white hover:text-gray-200 text-2xl"
//                   type="button"
//                 >
//                   &times;
//                 </button>
//               </div>
//             </div>
//             <div className="p-6 overflow-y-auto max-h-[80vh]">
//               {error && (
//                 <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//                   {error}
//                 </div>
//               )}
//               {step === 1 ? (
//                 showAddressForm ? (
//                   <form onSubmit={handleAddressSubmit}>
//                     <div className="space-y-4">
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Full Name*
//                         </label>
//                         <div className="relative">
//                           <FiUser className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="text"
//                             name="fullName"
//                             value={formData.fullName}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="John Doe"
//                           />
//                         </div>
//                       </div>
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Phone Number*
//                         </label>
//                         <div className="relative">
//                           <FiPhone className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="tel"
//                             name="phone"
//                             value={formData.phone}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Phone number"
//                             pattern="[0-9]{10}"
//                             maxLength="10"
//                           />
//                         </div>
//                       </div>
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Address Line 1*
//                         </label>
//                         <div className="relative">
//                           <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="text"
//                             name="addressLine1"
//                             value={formData.addressLine1}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Street address, P.O. box"
//                           />
//                         </div>
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Address Line 2
//                         </label>
//                         <input
//                           type="text"
//                           name="addressLine2"
//                           value={formData.addressLine2}
//                           onChange={handleChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                           placeholder="Apartment, suite, unit, building"
//                         />
//                       </div>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             City*
//                           </label>
//                           <input
//                             type="text"
//                             name="city"
//                             value={formData.city}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="City"
//                           />
//                         </div>
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             State*
//                           </label>
//                           <input
//                             type="text"
//                             name="state"
//                             value={formData.state}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="State"
//                           />
//                         </div>
//                       </div>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             ZIP Code*
//                           </label>
//                           <input
//                             type="text"
//                             name="zipCode"
//                             value={formData.zipCode}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Postal code"
//                           />
//                         </div>
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             Country*
//                           </label>
//                           <input
//                             type="text"
//                             name="country"
//                             value={formData.country}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Country"
//                           />
//                         </div>
//                       </div>
//                       <div className="flex items-center">
//                         <input
//                           type="checkbox"
//                           id="isDefault"
//                           name="isDefault"
//                           checked={formData.isDefault}
//                           onChange={handleChange}
//                           className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                         />
//                         <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                           Set as default shipping address
//                         </label>
//                       </div>
//                     </div>
//                     <div className="mt-6 flex justify-end space-x-3">
//                       <button
//                         type="button"
//                         onClick={() => setShowAddressForm(false)}
//                         className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                         disabled={isSubmitting}
//                       >
//                         <FiArrowLeft className="mr-1" /> Back
//                       </button>
//                       <button
//                         type="submit"
//                         className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                         disabled={isSubmitting}
//                       >
//                         {isSubmitting ? "Saving..." : "Save Address"}
//                       </button>
//                     </div>
//                     </form>
//                 ) : (
//                   <div>
//                     <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                       <FiMapPin /> Select Shipping Address
//                     </h3>
//                     {isLoadingAddresses ? (
//                       <div className="flex justify-center py-8">
//                         <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                       </div>
//                     ) : addresses.length === 0 ? (
//                       <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                         <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                         <p className="text-gray-600 mb-4">No saved addresses found</p>
//                         <button
//                           onClick={() => setShowAddressForm(true)}
//                           className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                         >
//                           <FiPlus className="mr-1" /> Add New Address
//                         </button>
//                       </div>
//                     ) : (
//                       <>
//                         <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                           <div className="grid grid-cols-1 gap-3">
//                             {addresses.map(address => (
//                               <div
//                                 key={address._id}
//                                 className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                                   selectedAddressId === address._id
//                                     ? 'border-blue-500 border-2 bg-blue-50 shadow-sm'
//                                     : 'border-gray-200 hover:border-gray-300'
//                                 }`}
//                                 onClick={() => setSelectedAddressId(address._id)}
//                               >
//                                 <div className="flex">
//                                   <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                     selectedAddressId === address._id
//                                       ? 'text-blue-500'
//                                       : 'text-gray-400'
//                                   }`}>
//                                     {selectedAddressId === address._id ? (
//                                       <FiCheck className="text-xl" />
//                                     ) : (
//                                       <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                     )}
//                                   </div>
//                                   <div className="flex-1 min-w-0">
//                                     {address.fullName && (
//                                       <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                     )}
//                                     <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                       {formatAddress(address)}
//                                     </p>
//                                     {address.isDefault && (
//                                       <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                         Default
//                                       </span>
//                                     )}
//                                   </div>
//                                 </div>
//                               </div>
//                             ))}
//                           </div>
//                         </div>
//                         <div className="flex flex-col sm:flex-row gap-3">
//                           <button
//                             onClick={() => setShowAddressForm(true)}
//                             className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                           >
//                             <FiPlus /> Add New Address
//                           </button>
//                           <button
//                             onClick={() => setStep(2)}
//                             className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                               selectedAddressId
//                                 ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90'
//                                 : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                             }`}
//                             disabled={!selectedAddressId}
//                           >
//                             Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                           </button>
//                         </div>
//                       </>
//                     )}
//                   </div>
//                 )
//               ) : (
//                 <form onSubmit={handlePaymentSubmit}>
//                   <div className="space-y-6">
//                     <div>
//                       <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                         <FiUser /> Select Payment Method
//                       </h3>
//                       <div className="space-y-3">
//                         <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                           paymentMethod === "cod"
//                             ? 'border-blue-500 bg-blue-50 shadow-sm'
//                             : 'border-gray-200 hover:border-blue-300'
//                         }`}>
//                           <input
//                             type="radio"
//                             name="paymentMethod"
//                             value="cod"
//                             checked={paymentMethod === "cod"}
//                             onChange={() => handlePaymentMethodChange("cod")}
//                             className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                           />
//                           <div className="ml-3">
//                             <span className="block font-medium">Cash on Delivery</span>
//                             <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                           </div>
//                         </label>
//                         <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                           paymentMethod === "razorpay"
//                             ? 'border-blue-500 bg-blue-50 shadow-sm'
//                             : 'border-gray-200 hover:border-blue-300'
//                         }`}>
//                           <input
//                             type="radio"
//                             name="paymentMethod"
//                             value="razorpay"
//                             checked={paymentMethod === "razorpay"}
//                             onChange={() => handlePaymentMethodChange("razorpay")}
//                             className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                           />
//                           <div className="ml-3">
//                             <span className="block font-medium">Pay with Razorpay</span>
//                             <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                           </div>
//                         </label>
//                       </div>
//                     </div>
                    
//                     {isLoadingCheckout ? (
//                       <div className="flex justify-center py-8">
//                         <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                       </div>
//                     ) : checkoutSummary ? (
//                       <div className="pt-4 border-t border-gray-200">
//                         <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                         <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Subtotal:</span>
//                             <span className="font-medium">₹{checkoutSummary.summary.subtotal?.toFixed(2)}</span>
//                           </div>
//                           {checkoutSummary.summary.taxes > 0 && (
//                             <div className="flex justify-between">
//                               <span className="text-gray-600">Taxes:</span>
//                               <span className="font-medium">₹{checkoutSummary.summary.taxes?.toFixed(2)}</span>
//                             </div>
//                           )}
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Shipping:</span>
//                             <span className="font-medium">₹{checkoutSummary.summary.shipping?.toFixed(2)}</span>
//                           </div>
//                           {checkoutSummary.summary.codCharge > 0 && (
//                             <div className="flex justify-between">
//                               <span className="text-gray-600">COD Charges:</span>
//                               <span className="font-medium">₹{checkoutSummary.summary.codCharge?.toFixed(2)}</span>
//                             </div>
//                           )}
//                           <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                             <span className="text-gray-800">Total:</span>
//                             <span className="text-lg text-blue-600">₹{checkoutSummary.summary.totalAmount?.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     ) : (
//                       <div className="pt-4 border-t border-gray-200">
//                         <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                         <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Subtotal:</span>
//                             <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Shipping:</span>
//                             <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                           </div>
//                           <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                             <span className="text-gray-800">Total:</span>
//                             <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                   <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                     <button
//                       type="button"
//                       onClick={handleBackToAddress}
//                       className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                       disabled={isSubmitting}
//                     >
//                       <FiArrowLeft /> Back to Address
//                     </button>
//                     <button
//                       type="submit"
//                       className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                       disabled={isSubmitting || isLoadingCheckout}
//                     >
//                       {isSubmitting ? (
//                         <>
//                           <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                           Processing...
//                         </>
//                       ) : (
//                         "Complete Order"
//                       )}
//                     </button>
//                   </div>
//                 </form>
//               )}
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import {
//   FiHome,
//   FiUser,
//   FiMapPin,
//   FiArrowLeft,
//   FiPlus,
//   FiCheck,
//   FiPhone,
//   FiCheckCircle
// } from "react-icons/fi";
// import { useNavigate } from "react-router-dom";

// const OrderSuccessModal = ({ order, onClose, onGoToOrders }) => (
//   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//     <div className="bg-white rounded-xl shadow-xl max-w-md w-full px-6 py-8 text-center">
//       <div className="flex flex-col items-center">
//         <FiCheckCircle className="text-green-500 text-5xl mb-3" />
//         <h2 className="text-2xl font-bold text-green-600 mb-2">Order Placed Successfully!</h2>
//         <p className="text-gray-700 mb-2">Thank you for shopping with us. Your order has been placed and is being processed.</p>
//         <div className="bg-gray-50 p-4 rounded-lg mb-4 w-full text-left mt-4">
//           <h3 className="font-semibold mb-1">Order Details:</h3>
//           <div className="text-sm text-gray-700">
//             <div><span className="font-medium">Order ID:</span> {order?._id || "N/A"}</div>
//             <div><span className="font-medium">Amount Paid:</span> ₹{order?.totalAmount?.toFixed(2) || "N/A"}</div>
//             <div><span className="font-medium">Payment Mode:</span> {order?.paymentMethod?.type === "cod" ? "Cash on Delivery" : "Online"}</div>
//             <div><span className="font-medium">Order Status:</span> {order?.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "Pending"}</div>
//             {order?.shippingAddress && (
//               <div className="mt-2">
//                 <span className="font-medium">Shipping To:</span> <br />
//                 <span className="text-gray-600">{[
//                   order.shippingAddress.fullName,
//                   order.shippingAddress.phone,
//                   order.shippingAddress.addressLine1,
//                   order.shippingAddress.addressLine2,
//                   `${order.shippingAddress.city}, ${order.shippingAddress.state} ${order.shippingAddress.zipCode}`,
//                   order.shippingAddress.country
//                 ].filter(Boolean).join(", ")}</span>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//       <div className="flex justify-center gap-3 mt-5">
//         <button
//           className="px-4 py-2 min-w-[120px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
//           onClick={onGoToOrders}
//         >
//           View Orders
//         </button>
//         <button
//           className="px-4 py-2 min-w-[120px] rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition"
//           onClick={onClose}
//         >
//           Close
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const ShippingAddress = ({
//   onClose,
//   onSubmit,
//   orderSummary,
//   cartItems,
//   clearCart,
//   fetchCart
// }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     phone: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
//   const [checkoutSummary, setCheckoutSummary] = useState(null);
//   const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

//   const [orderSuccess, setOrderSuccess] = useState(false);
//   const [placedOrder, setPlacedOrder] = useState(null);

//   const { user } = useSelector(state => state.auth);
//   const navigate = useNavigate();

//   const fetchAddresses = async () => {
//     const token = Cookies.get("ecom_user_token");
//     setIsLoadingAddresses(true);
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );
//       const receivedAddresses = response.data?.shippingAddresses || [];
//       const sortedAddresses = [...receivedAddresses].sort((a, b) => {
//         if (a.isDefault !== b.isDefault) return b.isDefault ? 1 : -1;
//         return new Date(b.createdAt) - new Date(a.createdAt);
//       });
//       setAddresses(sortedAddresses);
//       const defaultAddress = sortedAddresses.find(addr => addr.isDefault) || sortedAddresses[0];
//       if (defaultAddress) setSelectedAddressId(defaultAddress._id);
//     } catch (err) {
//       console.error("Error fetching addresses:", err);
//       toast.error(err.response?.data?.msg || "Failed to load addresses");
//       setAddresses([]);
//     } finally {
//       setIsLoadingAddresses(false);
//     }
//   };

//   const fetchCheckoutSummary = async () => {
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       return;
//     }
    
//     setIsLoadingCheckout(true);
//     setError("");
//     const token = Cookies.get("ecom_user_token");
    
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/checkout-summary`,
//         {
//           shippingAddressId: selectedAddressId,
//           paymentMethod: {
//           type: paymentMethod
//         }
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
      
//       if (response.data.success) {
//         setCheckoutSummary(response.data);
//       } else {
//         throw new Error(response.data.message || "Failed to fetch checkout summary");
//       }
//     } catch (err) {
//       console.error("Error fetching checkout summary:", err);
//       const errorMsg = err.response?.data?.message || err.response?.data?.message || "Failed to load checkout details";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsLoadingCheckout(false);
//     }
//   };

//   useEffect(() => {
//     if (step === 1) {
//       fetchAddresses();
//     }
//     // eslint-disable-next-line
//   }, [step]);

//   useEffect(() => {
//     if (step === 2 && selectedAddressId) {
//       fetchCheckoutSummary();
//     }
//     // eslint-disable-next-line
//   }, [step, selectedAddressId, paymentMethod]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev, [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-shipping-address`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       if (response.status === 201) {
//         const updatedAddresses = response.data.shippingAddresses || [];
//         if (!Array.isArray(updatedAddresses)) {
//           throw new Error("Invalid address data received");
//         }
//         const newAddress = updatedAddresses.reduce((latest, current) => {
//           if (!latest) return current;
//           return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
//         }, null);
//         if (!newAddress || !newAddress._id) throw new Error("New address not found in response");
//         setAddresses(updatedAddresses);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
//         setFormData({
//           fullName: "",
//           phone: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });
//         toast.success(response.data.msg || "Address saved successfully!");
//       }
//     } catch (err) {
//       console.error("Address save error:", { error: err, response: err.response?.data });
//       const errorMsg = err.response?.data?.msg ||
//         err.response?.data?.message || err.message || "Could not save address. Please try again.";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: { type: paymentMethod },
//         shippingCost: checkoutSummary?.summary?.shipping || orderSummary.shippingCharges
//       };
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         setOrderSuccess(true);
//         setPlacedOrder(response.data.order);
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: { type: "razorpay" },
//         shippingCost: checkoutSummary?.summary?.shipping || orderSummary.shippingCharges
//       };
//       const orderResponse = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);
//       await loadRazorpay();
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function (response) {
//           try {
//             const verificationResponse = await axios.post(
//               `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify-payment`,
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );
//             if (verificationResponse?.status === 200) {
//               setOrderSuccess(true);
//               setPlacedOrder(verificationResponse.data.order);
//               clearCart();
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: { color: "#3399cc" },
//         modal: {
//           ondismiss: function () {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };
//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function (response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     if (!address) return "";
//     return [
//       address.fullName,
//       address.phone,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   const handleBackToAddress = () => {
//     setStep(1);
//     setShowAddressForm(false);
//     setCheckoutSummary(null);
//     if (step === 2) {
//       setFormData({
//         fullName: "",
//         phone: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
//     }
//   };

//   const handleGoToOrders = () => {
//     setOrderSuccess(false);
//     onClose();
//     navigate("/profile/orders");
//   };

//   const handleSuccessClose = () => {
//     setOrderSuccess(false);
//     onClose();
//   };

//   const handlePaymentMethodChange = (method) => {
//     setPaymentMethod(method);
//     // Refetch checkout summary when payment method changes
//     if (selectedAddressId) {
//       fetchCheckoutSummary();
//     }
//   };

//   return (
//     <>
//       {orderSuccess && placedOrder && (
//         <OrderSuccessModal
//           order={placedOrder}
//           onClose={handleSuccessClose}
//           onGoToOrders={handleGoToOrders}
//         />
//       )}
//       {!orderSuccess && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//               step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//             }`}>
//             <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//               <div className="flex justify-between items-center">
//                 <h2 className="text-xl font-bold flex items-center gap-2">
//                   {step === 1 ? (
//                     showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//                   ) : (
//                     <><FiUser /> Payment Method</>
//                   )}
//                 </h2>
//                 <button
//                   onClick={onClose}
//                   className="text-white hover:text-gray-200 text-2xl"
//                   type="button"
//                 >
//                   &times;
//                 </button>
//               </div>
//             </div>
//             <div className="p-6 overflow-y-auto max-h-[80vh]">
//               {error && (
//                 <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//                   {error}
//                 </div>
//               )}
//               {step === 1 ? (
//                 showAddressForm ? (
//                   <form onSubmit={handleAddressSubmit}>
//                     <div className="space-y-4">
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Full Name*
//                         </label>
//                         <div className="relative">
//                           <FiUser className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="text"
//                             name="fullName"
//                             value={formData.fullName}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="John Doe"
//                           />
//                         </div>
//                       </div>
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Phone Number*
//                         </label>
//                         <div className="relative">
//                           <FiPhone className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="tel"
//                             name="phone"
//                             value={formData.phone}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Phone number"
//                             pattern="[0-9]{10}"
//                             maxLength="10"
//                           />
//                         </div>
//                       </div>
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Address Line 1*
//                         </label>
//                         <div className="relative">
//                           <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="text"
//                             name="addressLine1"
//                             value={formData.addressLine1}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Street address, P.O. box"
//                           />
//                         </div>
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Address Line 2
//                         </label>
//                         <input
//                           type="text"
//                           name="addressLine2"
//                           value={formData.addressLine2}
//                           onChange={handleChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                           placeholder="Apartment, suite, unit, building"
//                         />
//                       </div>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             City*
//                           </label>
//                           <input
//                             type="text"
//                             name="city"
//                             value={formData.city}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="City"
//                           />
//                         </div>
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             State*
//                           </label>
//                           <input
//                             type="text"
//                             name="state"
//                             value={formData.state}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="State"
//                           />
//                         </div>
//                       </div>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             ZIP Code*
//                           </label>
//                           <input
//                             type="text"
//                             name="zipCode"
//                             value={formData.zipCode}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Postal code"
//                           />
//                         </div>
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             Country*
//                           </label>
//                           <input
//                             type="text"
//                             name="country"
//                             value={formData.country}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Country"
//                           />
//                         </div>
//                       </div>
//                       <div className="flex items-center">
//                         <input
//                           type="checkbox"
//                           id="isDefault"
//                           name="isDefault"
//                           checked={formData.isDefault}
//                           onChange={handleChange}
//                           className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                         />
//                         <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                           Set as default shipping address
//                         </label>
//                       </div>
//                     </div>
//                     <div className="mt-6 flex justify-end space-x-3">
//                       <button
//                         type="button"
//                         onClick={() => setShowAddressForm(false)}
//                         className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                         disabled={isSubmitting}
//                       >
//                         <FiArrowLeft className="mr-1" /> Back
//                       </button>
//                       <button
//                         type="submit"
//                         className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                         disabled={isSubmitting}
//                       >
//                         {isSubmitting ? "Saving..." : "Save Address"}
//                       </button>
//                     </div>
//                     </form>
//                 ) : (
//                   <div>
//                     <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                       <FiMapPin /> Select Shipping Address
//                     </h3>
//                     {isLoadingAddresses ? (
//                       <div className="flex justify-center py-8">
//                         <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                       </div>
//                     ) : addresses.length === 0 ? (
//                       <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                         <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                         <p className="text-gray-600 mb-4">No saved addresses found</p>
//                         <button
//                           onClick={() => setShowAddressForm(true)}
//                           className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                         >
//                           <FiPlus className="mr-1" /> Add New Address
//                         </button>
//                       </div>
//                     ) : (
//                       <>
//                         <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                           <div className="grid grid-cols-1 gap-3">
//                             {addresses.map(address => (
//                               <div
//                                 key={address._id}
//                                 className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                                   selectedAddressId === address._id
//                                     ? 'border-blue-500 border-2 bg-blue-50 shadow-sm'
//                                     : 'border-gray-200 hover:border-gray-300'
//                                 }`}
//                                 onClick={() => setSelectedAddressId(address._id)}
//                               >
//                                 <div className="flex">
//                                   <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                     selectedAddressId === address._id
//                                       ? 'text-blue-500'
//                                       : 'text-gray-400'
//                                   }`}>
//                                     {selectedAddressId === address._id ? (
//                                       <FiCheck className="text-xl" />
//                                     ) : (
//                                       <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                     )}
//                                   </div>
//                                   <div className="flex-1 min-w-0">
//                                     {address.fullName && (
//                                       <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                     )}
//                                     <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                       {formatAddress(address)}
//                                     </p>
//                                     {address.isDefault && (
//                                       <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                         Default
//                                       </span>
//                                     )}
//                                   </div>
//                                 </div>
//                               </div>
//                             ))}
//                           </div>
//                         </div>
//                         <div className="flex flex-col sm:flex-row gap-3">
//                           <button
//                             onClick={() => setShowAddressForm(true)}
//                             className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                           >
//                             <FiPlus /> Add New Address
//                           </button>
//                           <button
//                             onClick={() => setStep(2)}
//                             className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                               selectedAddressId
//                                 ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90'
//                                 : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                             }`}
//                             disabled={!selectedAddressId}
//                           >
//                             Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                           </button>
//                         </div>
//                       </>
//                     )}
//                   </div>
//                 )
//               ) : (
//                 <form onSubmit={handlePaymentSubmit}>
//                   <div className="space-y-6">
//                     <div>
//                       <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                         <FiUser /> Select Payment Method
//                       </h3>
//                       <div className="space-y-3">
//                         <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                           paymentMethod === "cod"
//                             ? 'border-blue-500 bg-blue-50 shadow-sm'
//                             : 'border-gray-200 hover:border-blue-300'
//                         }`}>
//                           <input
//                             type="radio"
//                             name="paymentMethod"
//                             value="cod"
//                             checked={paymentMethod === "cod"}
//                             onChange={() => handlePaymentMethodChange("cod")}
//                             className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                           />
//                           <div className="ml-3">
//                             <span className="block font-medium">Cash on Delivery</span>
//                             <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                           </div>
//                         </label>
//                         <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                           paymentMethod === "razorpay"
//                             ? 'border-blue-500 bg-blue-50 shadow-sm'
//                             : 'border-gray-200 hover:border-blue-300'
//                         }`}>
//                           <input
//                             type="radio"
//                             name="paymentMethod"
//                             value="razorpay"
//                             checked={paymentMethod === "razorpay"}
//                             onChange={() => handlePaymentMethodChange("razorpay")}
//                             className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                           />
//                           <div className="ml-3">
//                             <span className="block font-medium">Pay with Razorpay</span>
//                             <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                           </div>
//                         </label>
//                       </div>
//                     </div>
                    
//                     {isLoadingCheckout ? (
//                       <div className="flex justify-center py-8">
//                         <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                       </div>
//                     ) : checkoutSummary ? (
//                       <div className="pt-4 border-t border-gray-200">
//                         <h3 className="text-lg font-medium mb-3">Order Summarys</h3>
//                         <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Subtotal:</span>
//                             <span className="font-medium">₹{checkoutSummary.summary.subtotal?.toFixed(2)}</span>
//                           </div>
//                           {checkoutSummary.summary.taxes > 0 && (
//                             <div className="flex justify-between">
//                               <span className="text-gray-600">Taxes:</span>
//                               <span className="font-medium">₹{checkoutSummary.summary.taxes?.toFixed(2)}</span>
//                             </div>
//                           )}
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Shipping:</span>
//                             <span className="font-medium">₹{checkoutSummary.summary.shipping?.toFixed(2)}</span>
//                           </div>
//                           {checkoutSummary.summary.codCharge > 0 && (
//                             <div className="flex justify-between">
//                               <span className="text-gray-600">COD Charges:</span>
//                               <span className="font-medium">₹{checkoutSummary.summary.codCharge?.toFixed(2)}</span>
//                             </div>
//                           )}
//                           <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                             <span className="text-gray-800">Total:</span>
//                             <span className="text-lg text-blue-600">₹{checkoutSummary.summary.totalAmount?.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     ) : (
//                       <div className="pt-4 border-t border-gray-200">
//                         <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                         <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Subtotal:</span>
//                             <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Shipping:</span>
//                             <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                           </div>
//                           <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                             <span className="text-gray-800">Total:</span>
//                             <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                   <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                     <button
//                       type="button"
//                       onClick={handleBackToAddress}
//                       className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                       disabled={isSubmitting}
//                     >
//                       <FiArrowLeft /> Back to Address
//                     </button>
//                     <button
//                       type="submit"
//                       className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                       disabled={isSubmitting || isLoadingCheckout}
//                     >
//                       {isSubmitting ? (
//                         <>
//                           <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                           Processing...
//                         </>
//                       ) : (
//                         "Complete Order"
//                       )}
//                     </button>
//                   </div>
//                 </form>
//               )}
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import {
//   FiHome,
//   FiUser,
//   FiMapPin,
//   FiArrowLeft,
//   FiPlus,
//   FiCheck,
//   FiPhone,
//   FiCheckCircle
// } from "react-icons/fi";
// import { useNavigate } from "react-router-dom";

// const OrderSuccessModal = ({ order, onClose, onGoToOrders }) => (
//   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//     <div className="bg-white rounded-xl shadow-xl max-w-md w-full px-6 py-8 text-center">
//       <div className="flex flex-col items-center">
//         <FiCheckCircle className="text-green-500 text-5xl mb-3" />
//         <h2 className="text-2xl font-bold text-green-600 mb-2">Order Placed Successfully!</h2>
//         <p className="text-gray-700 mb-2">Thank you for shopping with us. Your order has been placed and is being processed.</p>
//         <div className="bg-gray-50 p-4 rounded-lg mb-4 w-full text-left mt-4">
//           <h3 className="font-semibold mb-1">Order Details:</h3>
//           <div className="text-sm text-gray-700">
//             <div><span className="font-medium">Order ID:</span> {order?._id || "N/A"}</div>
//             <div><span className="font-medium">Amount Paid:</span> ₹{order?.totalAmount?.toFixed(2) || "N/A"}</div>
//             <div><span className="font-medium">Payment Mode:</span> {order?.paymentMethod?.type === "cod" ? "Cash on Delivery" : "Online"}</div>
//             <div><span className="font-medium">Order Status:</span> {order?.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : "Pending"}</div>
//             {order?.shippingAddress && (
//               <div className="mt-2">
//                 <span className="font-medium">Shipping To:</span> <br />
//                 <span className="text-gray-600">{[
//                   order.shippingAddress.fullName,
//                   order.shippingAddress.phone,
//                   order.shippingAddress.addressLine1,
//                   order.shippingAddress.addressLine2,
//                   `${order.shippingAddress.city}, ${order.shippingAddress.state} ${order.shippingAddress.zipCode}`,
//                   order.shippingAddress.country
//                 ].filter(Boolean).join(", ")}</span>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//       <div className="flex justify-center gap-3 mt-5">
//         <button
//           className="px-4 py-2 min-w-[120px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
//           onClick={onGoToOrders}
//         >
//           View Orders
//         </button>
//         <button
//           className="px-4 py-2 min-w-[120px] rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition"
//           onClick={onClose}
//         >
//           Close
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const ShippingAddress = ({
//   onClose,
//   onSubmit,
//   orderSummary,
//   cartItems,
//   clearCart,
//   fetchCart
// }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     phone: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const [orderSuccess, setOrderSuccess] = useState(false);
//   const [placedOrder, setPlacedOrder] = useState(null);

//   const { user } = useSelector(state => state.auth);
//   const navigate = useNavigate();

//   const fetchAddresses = async () => {
//     const token = Cookies.get("ecom_user_token");
//     setIsLoadingAddresses(true);
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );
//       const receivedAddresses = response.data?.shippingAddresses || [];
//       const sortedAddresses = [...receivedAddresses].sort((a, b) => {
//         if (a.isDefault !== b.isDefault) return b.isDefault ? 1 : -1;
//         return new Date(b.createdAt) - new Date(a.createdAt);
//       });
//       setAddresses(sortedAddresses);
//       const defaultAddress = sortedAddresses.find(addr => addr.isDefault) || sortedAddresses[0];
//       if (defaultAddress) setSelectedAddressId(defaultAddress._id);
//     } catch (err) {
//       console.error("Error fetching addresses:", err);
//       toast.error(err.response?.data?.msg || "Failed to load addresses");
//       setAddresses([]);
//     } finally {
//       setIsLoadingAddresses(false);
//     }
//   };

//   useEffect(() => {
//     if (step === 1) {
//       fetchAddresses();
//     }
//     // eslint-disable-next-line
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev, [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-shipping-address`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       if (response.status === 201) {
//         const updatedAddresses = response.data.shippingAddresses || [];
//         if (!Array.isArray(updatedAddresses)) {
//           throw new Error("Invalid address data received");
//         }
//         const newAddress = updatedAddresses.reduce((latest, current) => {
//           if (!latest) return current;
//           return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
//         }, null);
//         if (!newAddress || !newAddress._id) throw new Error("New address not found in response");
//         setAddresses(updatedAddresses);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
//         setFormData({
//           fullName: "",
//           phone: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });
//         toast.success(response.data.msg || "Address saved successfully!");
//       }
//     } catch (err) {
//       console.error("Address save error:", { error: err, response: err.response?.data });
//       const errorMsg = err.response?.data?.msg ||
//         err.response?.data?.message || err.message || "Could not save address. Please try again.";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: { type: paymentMethod },
//         shippingCost: orderSummary.shippingCharges
//       };
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         setOrderSuccess(true);
//         setPlacedOrder(response.data.order);
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: { type: "razorpay" },
//         shippingCost: orderSummary.shippingCharges
//       };
//       const orderResponse = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);
//       await loadRazorpay();
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function (response) {
//           try {
//             const verificationResponse = await axios.post(
//               `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify-payment`,
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );
//             if (verificationResponse?.status === 200) {
//               setOrderSuccess(true);
//               setPlacedOrder(verificationResponse.data.order);
//               clearCart();
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: { color: "#3399cc" },
//         modal: {
//           ondismiss: function () {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };
//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function (response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     if (!address) return "";
//     return [
//       address.fullName,
//       address.phone,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   const handleBackToAddress = () => {
//     setStep(1);
//     setShowAddressForm(false);
//     if (step === 2) {
//       setFormData({
//         fullName: "",
//         phone: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
//     }
//   };

//   const handleGoToOrders = () => {
//     setOrderSuccess(false);
//     onClose();
//     navigate("/profile/orders");
//   };

//   const handleSuccessClose = () => {
//     setOrderSuccess(false);
//     onClose();
//   };

//   return (
//     <>
//       {orderSuccess && placedOrder && (
//         <OrderSuccessModal
//           order={placedOrder}
//           onClose={handleSuccessClose}
//           onGoToOrders={handleGoToOrders}
//         />
//       )}
//       {!orderSuccess && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//               step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//             }`}>
//             <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//               <div className="flex justify-between items-center">
//                 <h2 className="text-xl font-bold flex items-center gap-2">
//                   {step === 1 ? (
//                     showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//                   ) : (
//                     <><FiUser /> Payment Method</>
//                   )}
//                 </h2>
//                 <button
//                   onClick={onClose}
//                   className="text-white hover:text-gray-200 text-2xl"
//                   type="button"
//                 >
//                   &times;
//                 </button>
//               </div>
//             </div>
//             <div className="p-6 overflow-y-auto max-h-[80vh]">
//               {error && (
//                 <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//                   {error}
//                 </div>
//               )}
//               {step === 1 ? (
//                 showAddressForm ? (
//                   <form onSubmit={handleAddressSubmit}>
//                     <div className="space-y-4">
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Full Name*
//                         </label>
//                         <div className="relative">
//                           <FiUser className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="text"
//                             name="fullName"
//                             value={formData.fullName}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="John Doe"
//                           />
//                         </div>
//                       </div>
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Phone Number*
//                         </label>
//                         <div className="relative">
//                           <FiPhone className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="tel"
//                             name="phone"
//                             value={formData.phone}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Phone number"
//                             pattern="[0-9]{10}"
//                             maxLength="10"
//                           />
//                         </div>
//                       </div>
//                       <div className="relative">
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Address Line 1*
//                         </label>
//                         <div className="relative">
//                           <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                           <input
//                             type="text"
//                             name="addressLine1"
//                             value={formData.addressLine1}
//                             onChange={handleChange}
//                             required
//                             className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Street address, P.O. box"
//                           />
//                         </div>
//                       </div>
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                           Address Line 2
//                         </label>
//                         <input
//                           type="text"
//                           name="addressLine2"
//                           value={formData.addressLine2}
//                           onChange={handleChange}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                           placeholder="Apartment, suite, unit, building"
//                         />
//                       </div>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             City*
//                           </label>
//                           <input
//                             type="text"
//                             name="city"
//                             value={formData.city}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="City"
//                           />
//                         </div>
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             State*
//                           </label>
//                           <input
//                             type="text"
//                             name="state"
//                             value={formData.state}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="State"
//                           />
//                         </div>
//                       </div>
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             ZIP Code*
//                           </label>
//                           <input
//                             type="text"
//                             name="zipCode"
//                             value={formData.zipCode}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Postal code"
//                           />
//                         </div>
//                         <div>
//                           <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                             Country*
//                           </label>
//                           <input
//                             type="text"
//                             name="country"
//                             value={formData.country}
//                             onChange={handleChange}
//                             required
//                             className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                             placeholder="Country"
//                           />
//                         </div>
//                       </div>
//                       <div className="flex items-center">
//                         <input
//                           type="checkbox"
//                           id="isDefault"
//                           name="isDefault"
//                           checked={formData.isDefault}
//                           onChange={handleChange}
//                           className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                         />
//                         <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                           Set as default shipping address
//                         </label>
//                       </div>
//                     </div>
//                     <div className="mt-6 flex justify-end space-x-3">
//                       <button
//                         type="button"
//                         onClick={() => setShowAddressForm(false)}
//                         className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                         disabled={isSubmitting}
//                       >
//                         <FiArrowLeft className="mr-1" /> Back
//                       </button>
//                       <button
//                         type="submit"
//                         className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                         disabled={isSubmitting}
//                       >
//                         {isSubmitting ? "Saving..." : "Save Address"}
//                       </button>
//                     </div>
//                   </form>
//                 ) : (
//                   <div>
//                     <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                       <FiMapPin /> Select Shipping Address
//                     </h3>
//                     {isLoadingAddresses ? (
//                       <div className="flex justify-center py-8">
//                         <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                       </div>
//                     ) : addresses.length === 0 ? (
//                       <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                         <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                         <p className="text-gray-600 mb-4">No saved addresses found</p>
//                         <button
//                           onClick={() => setShowAddressForm(true)}
//                           className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                         >
//                           <FiPlus className="mr-1" /> Add New Address
//                         </button>
//                       </div>
//                     ) : (
//                       <>
//                         <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                           <div className="grid grid-cols-1 gap-3">
//                             {addresses.map(address => (
//                               <div
//                                 key={address._id}
//                                 className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                                   selectedAddressId === address._id
//                                     ? 'border-blue-500 border-2 bg-blue-50 shadow-sm'
//                                     : 'border-gray-200 hover:border-gray-300'
//                                 }`}
//                                 onClick={() => setSelectedAddressId(address._id)}
//                               >
//                                 <div className="flex">
//                                   <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                     selectedAddressId === address._id
//                                       ? 'text-blue-500'
//                                       : 'text-gray-400'
//                                   }`}>
//                                     {selectedAddressId === address._id ? (
//                                       <FiCheck className="text-xl" />
//                                     ) : (
//                                       <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                     )}
//                                   </div>
//                                   <div className="flex-1 min-w-0">
//                                     {address.fullName && (
//                                       <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                     )}
//                                     <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                       {formatAddress(address)}
//                                     </p>
//                                     {address.isDefault && (
//                                       <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                         Default
//                                       </span>
//                                     )}
//                                   </div>
//                                 </div>
//                               </div>
//                             ))}
//                           </div>
//                         </div>
//                         <div className="flex flex-col sm:flex-row gap-3">
//                           <button
//                             onClick={() => setShowAddressForm(true)}
//                             className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                           >
//                             <FiPlus /> Add New Address
//                           </button>
//                           <button
//                             onClick={() => setStep(2)}
//                             className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                               selectedAddressId
//                                 ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90'
//                                 : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                             }`}
//                             disabled={!selectedAddressId}
//                           >
//                             Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                           </button>
//                         </div>
//                       </>
//                     )}
//                   </div>
//                 )
//               ) : (
//                 <form onSubmit={handlePaymentSubmit}>
//                   <div className="space-y-6">
//                     <div>
//                       <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                         <FiUser /> Select Payment Method
//                       </h3>
//                       <div className="space-y-3">
//                         <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                           paymentMethod === "cod"
//                             ? 'border-blue-500 bg-blue-50 shadow-sm'
//                             : 'border-gray-200 hover:border-blue-300'
//                         }`}>
//                           <input
//                             type="radio"
//                             name="paymentMethod"
//                             value="cod"
//                             checked={paymentMethod === "cod"}
//                             onChange={() => setPaymentMethod("cod")}
//                             className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                           />
//                           <div className="ml-3">
//                             <span className="block font-medium">Cash on Delivery</span>
//                             <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                           </div>
//                         </label>
//                         <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                           paymentMethod === "razorpay"
//                             ? 'border-blue-500 bg-blue-50 shadow-sm'
//                             : 'border-gray-200 hover:border-blue-300'
//                         }`}>
//                           <input
//                             type="radio"
//                             name="paymentMethod"
//                             value="razorpay"
//                             checked={paymentMethod === "razorpay"}
//                             onChange={() => setPaymentMethod("razorpay")}
//                             className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                           />
//                           <div className="ml-3">
//                             <span className="block font-medium">Pay with Razorpay</span>
//                             <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                           </div>
//                         </label>
//                       </div>
//                     </div>
//                     <div className="pt-4 border-t border-gray-200">
//                       <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                       <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                         <div className="flex justify-between">
//                           <span className="text-gray-600">Subtotal:</span>
//                           <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                         </div>
//                         <div className="flex justify-between">
//                           <span className="text-gray-600">Shipping:</span>
//                           <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                         </div>
//                         <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                           <span className="text-gray-800">Total:</span>
//                           <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                     <button
//                       type="button"
//                       onClick={handleBackToAddress}
//                       className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                       disabled={isSubmitting}
//                     >
//                       <FiArrowLeft /> Back to Address
//                     </button>
//                     <button
//                       type="submit"
//                       className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                       disabled={isSubmitting}
//                     >
//                       {isSubmitting ? (
//                         <>
//                           <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                           Processing...
//                         </>
//                       ) : (
//                         "Complete Order"
//                       )}
//                     </button>
//                   </div>
//                 </form>
//               )}
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default ShippingAddress;


// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import { 
//   FiHome, 
//   FiUser, 
//   FiMapPin, 
//   FiArrowLeft, 
//   FiPlus, 
//   FiCheck,
//   FiPhone 
// } from "react-icons/fi";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart, fetchCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     phone: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [orderDetails, setOrderDetails] = useState(null);

//   const { user } = useSelector(state => state.auth);

//   const fetchAddresses = async () => {
//     const token = Cookies.get("ecom_user_token");
//     setIsLoadingAddresses(true);
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       const receivedAddresses = response.data?.shippingAddresses || [];
      
//       const sortedAddresses = [...receivedAddresses].sort((a, b) => {
//         if (a.isDefault !== b.isDefault) {
//           return b.isDefault ? 1 : -1;
//         }
//         return new Date(b.createdAt) - new Date(a.createdAt);
//       });

//       setAddresses(sortedAddresses);
      
//       const defaultAddress = sortedAddresses.find(addr => addr.isDefault) || sortedAddresses[0];
//       if (defaultAddress) {
//         setSelectedAddressId(defaultAddress._id);
//       }
//     } catch (err) {
//       console.error("Error fetching addresses:", err);
//       toast.error(err.response?.data?.msg || "Failed to load addresses");
//       setAddresses([]);
//     } finally {
//       setIsLoadingAddresses(false);
//     }
//   };

//   useEffect(() => {
//     if (step === 1) {
//       fetchAddresses();
//     }
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-shipping-address`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         const updatedAddresses = response.data.shippingAddresses || [];
        
//         if (!Array.isArray(updatedAddresses)) {
//           throw new Error("Invalid address data received");
//         }

//         const newAddress = updatedAddresses.reduce((latest, current) => {
//           if (!latest) return current;
//           return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
//         }, null);

//         if (!newAddress || !newAddress._id) {
//           throw new Error("New address not found in response");
//         }

//         setAddresses(updatedAddresses);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
        
//         setFormData({
//           fullName: "",
//           phone: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });

//         toast.success(response.data.msg || "Address saved successfully!");
//       }
//     } catch (err) {
//       console.error("Address save error:", {
//         error: err,
//         response: err.response?.data
//       });

//       const errorMsg = err.response?.data?.msg || 
//                       err.response?.data?.message || 
//                       err.message || 
//                       "Could not save address. Please try again.";
      
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setOrderDetails(response.data.order);
//         setShowSuccessModal(true);
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success(`Order placed successfully with ${paymentMethod === "cod" ? "COD" : "online payment"}!`);
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//       await loadRazorpay();
      
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             const verificationResponse = await axios.post(
//               `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify-payment`,
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               setOrderDetails(verificationResponse.data.order);
//               setShowSuccessModal(true);
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               clearCart(); 
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     if (!address) return "";
//     if (typeof address === 'string') return address;
    
//     return [
//       address.fullName,
//       address.phone,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   const handleBackToAddress = () => {
//     setStep(1);
//     setShowAddressForm(false);
//     if (step === 2) {
//       setFormData({
//         fullName: "",
//         phone: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//         step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//       }`}>
//         <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//           <div className="flex justify-between items-center">
//             <h2 className="text-xl font-bold flex items-center gap-2">
//               {step === 1 ? (
//                 showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//               ) : (
//                 <><FiUser /> Payment Method</>
//               )}
//             </h2>
//             <button 
//               onClick={onClose} 
//               className="text-white hover:text-gray-200 text-2xl"
//             >
//               &times;
//             </button>
//           </div>
//         </div>

//         <div className="p-6 overflow-y-auto max-h-[80vh]">
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//               {error}
//             </div>
//           )}

//           {step === 1 ? (
//             showAddressForm ? (
//               <form onSubmit={handleAddressSubmit}>
//                 <div className="space-y-4">
//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Full Name*
//                     </label>
//                     <div className="relative">
//                       <FiUser className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="fullName"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="John Doe"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Phone Number*
//                     </label>
//                     <div className="relative">
//                       <FiPhone className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="tel"
//                         name="phone"
//                         value={formData.phone}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Phone number"
//                         pattern="[0-9]{10}"
//                         maxLength="10"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 1*
//                     </label>
//                     <div className="relative">
//                       <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="addressLine1"
//                         value={formData.addressLine1}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Street address, P.O. box"
//                       />
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 2
//                     </label>
//                     <input
//                       type="text"
//                       name="addressLine2"
//                       value={formData.addressLine2}
//                       onChange={handleChange}
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Apartment, suite, unit, building"
//                     />
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         City*
//                       </label>
//                       <input
//                         type="text"
//                         name="city"
//                         value={formData.city}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="City"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         State*
//                       </label>
//                       <input
//                         type="text"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         ZIP Code*
//                       </label>
//                       <input
//                         type="text"
//                         name="zipCode"
//                         value={formData.zipCode}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Postal code"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         Country*
//                       </label>
//                       <input
//                         type="text"
//                         name="country"
//                         value={formData.country}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Country"
//                       />
//                     </div>
//                   </div>

//                   <div className="flex items-center">
//                     <input
//                       type="checkbox"
//                       id="isDefault"
//                       name="isDefault"
//                       checked={formData.isDefault}
//                       onChange={handleChange}
//                       className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                     />
//                     <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                       Set as default shipping address
//                     </label>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowAddressForm(false)}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     <FiArrowLeft className="mr-1" /> Back
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     {isSubmitting ? "Saving..." : "Save Address"}
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               <div>
//                 <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                   <FiMapPin /> Select Shipping Address
//                 </h3>
                
//                 {isLoadingAddresses ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                   </div>
//                 ) : addresses.length === 0 ? (
//                   <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                     <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                     <p className="text-gray-600 mb-4">No saved addresses found</p>
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                     >
//                       <FiPlus className="mr-1" /> Add New Address
//                     </button>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                       <div className="grid grid-cols-1 gap-3">
//                         {addresses.map(address => (
//                           <div 
//                             key={address._id}
//                             className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                               selectedAddressId === address._id 
//                                 ? 'border-blue-500 border-2 bg-blue-50 shadow-sm' 
//                                 : 'border-gray-200 hover:border-gray-300'
//                             }`}
//                             onClick={() => setSelectedAddressId(address._id)}
//                           >
//                             <div className="flex">
//                               <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                 selectedAddressId === address._id 
//                                   ? 'text-blue-500' 
//                                   : 'text-gray-400'
//                               }`}>
//                                 {selectedAddressId === address._id ? (
//                                   <FiCheck className="text-xl" />
//                                 ) : (
//                                   <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                 )}
//                               </div>
//                               <div className="flex-1 min-w-0">
//                                 {address.fullName && (
//                                   <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                 )}
//                                 <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                   {formatAddress(address)}
//                                 </p>
//                                 {address.isDefault && (
//                                   <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                     Default
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
                    
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <button
//                         onClick={() => setShowAddressForm(true)}
//                         className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                       >
//                         <FiPlus /> Add New Address
//                       </button>
//                       <button
//                         onClick={() => setStep(2)}
//                         className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                           selectedAddressId 
//                             ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90' 
//                             : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                         }`}
//                         disabled={!selectedAddressId}
//                       >
//                         Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             )
//           ) : (
//             <form onSubmit={handlePaymentSubmit}>
//               <div className="space-y-6">
//                 <div>
//                   <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                     <FiUser /> Select Payment Method
//                   </h3>
//                   <div className="space-y-3">
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "cod" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="cod"
//                         checked={paymentMethod === "cod"}
//                         onChange={() => setPaymentMethod("cod")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Cash on Delivery</span>
//                         <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                       </div>
//                     </label>
                    
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "razorpay" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="razorpay"
//                         checked={paymentMethod === "razorpay"}
//                         onChange={() => setPaymentMethod("razorpay")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Pay with Razorpay</span>
//                         <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                       </div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="pt-4 border-t border-gray-200">
//                   <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                   <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal:</span>
//                       <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Shipping:</span>
//                       <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                       <span className="text-gray-800">Total:</span>
//                       <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                 <button
//                   type="button"
//                   onClick={handleBackToAddress}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   <FiArrowLeft /> Back to Address
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? (
//                     <>
//                       <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                       Processing...
//                     </>
//                   ) : (
//                     "Complete Order"
//                   )}
//                 </button>
//               </div>
//             </form>
//           )}
//         </div>
//       </div>

//       {/* Success Modal */}
//       {showSuccessModal && orderDetails && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
//             <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 text-white">
//               <div className="flex justify-between items-center">
//                 <h2 className="text-xl font-bold flex items-center gap-2">
//                   <FiCheck className="text-2xl" /> Order Successful!
//                 </h2>
//                 <button 
//                   onClick={() => {
//                     setShowSuccessModal(false);
//                     onClose();
//                   }} 
//                   className="text-white hover:text-gray-200 text-2xl"
//                 >
//                   &times;
//                 </button>
//               </div>
//             </div>

//             <div className="p-6">
//               <div className="text-center mb-6">
//                 <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
//                   <FiCheck className="h-6 w-6 text-green-600" />
//                 </div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-2">
//                   Thank you for your order!
//                 </h3>
//                 <p className="text-sm text-gray-500">
//                   Your order #{orderDetails.orderId} has been placed successfully.
//                 </p>
//               </div>

//               <div className="border-t border-gray-200 pt-4 mb-6">
//                 <h4 className="font-medium text-gray-900 mb-3">Order Summary</h4>
//                 <div className="space-y-3 text-sm">
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">Order Number:</span>
//                     <span className="font-medium">#{orderDetails.orderId}</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">Date:</span>
//                     <span className="font-medium">
//                       {new Date(orderDetails.createdAt).toLocaleDateString()}
//                     </span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">Total Amount:</span>
//                     <span className="font-medium">₹{orderDetails.totalAmount?.toFixed(2)}</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span className="text-gray-600">Payment Method:</span>
//                     <span className="font-medium capitalize">
//                       {orderDetails.paymentMethod.type === "cod" ? "Cash on Delivery" : "Online Payment"}
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               <div className="border-t border-gray-200 pt-4">
//                 <h4 className="font-medium text-gray-900 mb-3">Shipping Address</h4>
//                 <p className="text-sm text-gray-600">
//                   {formatAddress(orderDetails.shippingAddress)}
//                 </p>
//               </div>

//               <div className="mt-6 flex flex-col sm:flex-row gap-3">
//                 <button
//                   onClick={() => {
//                     setShowSuccessModal(false);
//                     onClose();
//                   }}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex-1"
//                 >
//                   Continue Shopping
//                 </button>
//                 <button
//                   onClick={() => {
//                     // Replace with your actual orders page route
//                     window.location.href = '/orders';
//                   }}
//                   className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex-1"
//                 >
//                   View Order Details
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import { 
//   FiHome, 
//   FiUser, 
//   FiMapPin, 
//   FiArrowLeft, 
//   FiPlus, 
//   FiCheck,
//   FiPhone 
// } from "react-icons/fi";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart, fetchCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     phone: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const { user } = useSelector(state => state.auth);

//   const fetchAddresses = async () => {
//     const token = Cookies.get("ecom_user_token");
//     setIsLoadingAddresses(true);
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-shipping-addresses`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       const receivedAddresses = response.data?.shippingAddresses || [];
      
//       const sortedAddresses = [...receivedAddresses].sort((a, b) => {
//         if (a.isDefault !== b.isDefault) {
//           return b.isDefault ? 1 : -1;
//         }
//         return new Date(b.createdAt) - new Date(a.createdAt);
//       });

//       setAddresses(sortedAddresses);
      
//       const defaultAddress = sortedAddresses.find(addr => addr.isDefault) || sortedAddresses[0];
//       if (defaultAddress) {
//         setSelectedAddressId(defaultAddress._id);
//       }
//     } catch (err) {
//       console.error("Error fetching addresses:", err);
//       toast.error(err.response?.data?.msg || "Failed to load addresses");
//       setAddresses([]);
//     } finally {
//       setIsLoadingAddresses(false);
//     }
//   };

//   useEffect(() => {
//     if (step === 1) {
//       fetchAddresses();
//     }
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-shipping-address`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         const updatedAddresses = response.data.shippingAddresses || [];
        
//         if (!Array.isArray(updatedAddresses)) {
//           throw new Error("Invalid address data received");
//         }

//         const newAddress = updatedAddresses.reduce((latest, current) => {
//           if (!latest) return current;
//           return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
//         }, null);

//         if (!newAddress || !newAddress._id) {
//           throw new Error("New address not found in response");
//         }

//         setAddresses(updatedAddresses);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
        
//         setFormData({
//           fullName: "",
//           phone: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });

//         toast.success(response.data.msg || "Address saved successfully!");
//       }
//     } catch (err) {
//       console.error("Address save error:", {
//         error: err,
//         response: err.response?.data
//       });

//       const errorMsg = err.response?.data?.msg || 
//                       err.response?.data?.message || 
//                       err.message || 
//                       "Could not save address. Please try again.";
      
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success(`Order placed successfully with ${paymentMethod === "cod" ? "COD" : "online payment"}!`);
//         onClose();
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/create-order`,
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//       await loadRazorpay();
      
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             const verificationResponse = await axios.post(
//               `${process.env.REACT_APP_BACKEND_URL}/api/payment/verify-payment`,
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               onClose();
//               clearCart(); 
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     if (!address) return "";
//     return [
//       address.fullName,
//       address.phone,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   const handleBackToAddress = () => {
//     setStep(1);
//     setShowAddressForm(false);
//     if (step === 2) {
//       setFormData({
//         fullName: "",
//         phone: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//         step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//       }`}>
//         <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//           <div className="flex justify-between items-center">
//             <h2 className="text-xl font-bold flex items-center gap-2">
//               {step === 1 ? (
//                 showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//               ) : (
//                 <><FiUser /> Payment Method</>
//               )}
//             </h2>
//             <button 
//               onClick={onClose} 
//               className="text-white hover:text-gray-200 text-2xl"
//             >
//               &times;
//             </button>
//           </div>
//         </div>

//         <div className="p-6 overflow-y-auto max-h-[80vh]">
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//               {error}
//             </div>
//           )}

//           {step === 1 ? (
//             showAddressForm ? (
//               <form onSubmit={handleAddressSubmit}>
//                 <div className="space-y-4">
//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Full Name*
//                     </label>
//                     <div className="relative">
//                       <FiUser className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="fullName"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="John Doe"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Phone Number*
//                     </label>
//                     <div className="relative">
//                       <FiPhone className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="tel"
//                         name="phone"
//                         value={formData.phone}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Phone number"
//                         pattern="[0-9]{10}"
//                         maxLength="10"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 1*
//                     </label>
//                     <div className="relative">
//                       <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="addressLine1"
//                         value={formData.addressLine1}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Street address, P.O. box"
//                       />
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 2
//                     </label>
//                     <input
//                       type="text"
//                       name="addressLine2"
//                       value={formData.addressLine2}
//                       onChange={handleChange}
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Apartment, suite, unit, building"
//                     />
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         City*
//                       </label>
//                       <input
//                         type="text"
//                         name="city"
//                         value={formData.city}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="City"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         State*
//                       </label>
//                       <input
//                         type="text"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         ZIP Code*
//                       </label>
//                       <input
//                         type="text"
//                         name="zipCode"
//                         value={formData.zipCode}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Postal code"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         Country*
//                       </label>
//                       <input
//                         type="text"
//                         name="country"
//                         value={formData.country}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Country"
//                       />
//                     </div>
//                   </div>

//                   <div className="flex items-center">
//                     <input
//                       type="checkbox"
//                       id="isDefault"
//                       name="isDefault"
//                       checked={formData.isDefault}
//                       onChange={handleChange}
//                       className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                     />
//                     <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                       Set as default shipping address
//                     </label>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowAddressForm(false)}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     <FiArrowLeft className="mr-1" /> Back
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     {isSubmitting ? "Saving..." : "Save Address"}
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               <div>
//                 <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                   <FiMapPin /> Select Shipping Address
//                 </h3>
                
//                 {isLoadingAddresses ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                   </div>
//                 ) : addresses.length === 0 ? (
//                   <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                     <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                     <p className="text-gray-600 mb-4">No saved addresses found</p>
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                     >
//                       <FiPlus className="mr-1" /> Add New Address
//                     </button>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                       <div className="grid grid-cols-1 gap-3">
//                         {addresses.map(address => (
//                           <div 
//                             key={address._id}
//                             className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                               selectedAddressId === address._id 
//                                 ? 'border-blue-500 border-2 bg-blue-50 shadow-sm' 
//                                 : 'border-gray-200 hover:border-gray-300'
//                             }`}
//                             onClick={() => setSelectedAddressId(address._id)}
//                           >
//                             <div className="flex">
//                               <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                 selectedAddressId === address._id 
//                                   ? 'text-blue-500' 
//                                   : 'text-gray-400'
//                               }`}>
//                                 {selectedAddressId === address._id ? (
//                                   <FiCheck className="text-xl" />
//                                 ) : (
//                                   <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                 )}
//                               </div>
//                               <div className="flex-1 min-w-0">
//                                 {address.fullName && (
//                                   <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                 )}
//                                 <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                   {formatAddress(address)}
//                                 </p>
//                                 {address.isDefault && (
//                                   <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                     Default
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
                    
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <button
//                         onClick={() => setShowAddressForm(true)}
//                         className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                       >
//                         <FiPlus /> Add New Address
//                       </button>
//                       <button
//                         onClick={() => setStep(2)}
//                         className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                           selectedAddressId 
//                             ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90' 
//                             : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                         }`}
//                         disabled={!selectedAddressId}
//                       >
//                         Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             )
//           ) : (
//             <form onSubmit={handlePaymentSubmit}>
//               <div className="space-y-6">
//                 <div>
//                   <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                     <FiUser /> Select Payment Method
//                   </h3>
//                   <div className="space-y-3">
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "cod" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="cod"
//                         checked={paymentMethod === "cod"}
//                         onChange={() => setPaymentMethod("cod")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Cash on Delivery</span>
//                         <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                       </div>
//                     </label>
                    
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "razorpay" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="razorpay"
//                         checked={paymentMethod === "razorpay"}
//                         onChange={() => setPaymentMethod("razorpay")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Pay with Razorpay</span>
//                         <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                       </div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="pt-4 border-t border-gray-200">
//                   <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                   <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal:</span>
//                       <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Shipping:</span>
//                       <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                       <span className="text-gray-800">Total:</span>
//                       <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                 <button
//                   type="button"
//                   onClick={handleBackToAddress}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   <FiArrowLeft /> Back to Address
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? (
//                     <>
//                       <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                       Processing...
//                     </>
//                   ) : (
//                     "Complete Order"
//                   )}
//                 </button>
//               </div>
//             </form>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import { FiHome, FiUser, FiMapPin, FiArrowLeft, FiPlus, FiCheck } from "react-icons/fi";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart, fetchCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const { user } = useSelector(state => state.auth);

//   // Fetch addresses with proper error handling
//   const fetchAddresses = async () => {
//     const token = Cookies.get("ecom_user_token");
//     setIsLoadingAddresses(true);
//     try {
//       const response = await axios.get(
//         "https://seller-backend-29o9.onrender.com/api/buyer/get-shipping-addresses",
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       const receivedAddresses = response.data?.shippingAddresses || [];
      
//       // Sort addresses: default first, then by creation date (newest first)
//       const sortedAddresses = [...receivedAddresses].sort((a, b) => {
//         if (a.isDefault !== b.isDefault) {
//           return b.isDefault ? 1 : -1;
//         }
//         return new Date(b.createdAt) - new Date(a.createdAt);
//       });

//       setAddresses(sortedAddresses);
      
//       // Select the default address or the first one if no default
//       const defaultAddress = sortedAddresses.find(addr => addr.isDefault) || sortedAddresses[0];
//       if (defaultAddress) {
//         setSelectedAddressId(defaultAddress._id);
//       }
//     } catch (err) {
//       console.error("Error fetching addresses:", err);
//       toast.error(err.response?.data?.msg || "Failed to load addresses");
//       setAddresses([]);
//     } finally {
//       setIsLoadingAddresses(false);
//     }
//   };

//   useEffect(() => {
//     if (step === 1) {
//       fetchAddresses();
//     }
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   // Handle address submission with proper response handling
//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         const updatedAddresses = response.data.shippingAddresses || [];
        
//         if (!Array.isArray(updatedAddresses)) {
//           throw new Error("Invalid address data received");
//         }

//         // Find the newly added address (likely the one with the latest createdAt)
//         const newAddress = updatedAddresses.reduce((latest, current) => {
//           if (!latest) return current;
//           return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
//         }, null);

//         if (!newAddress || !newAddress._id) {
//           throw new Error("New address not found in response");
//         }

//         // Update state with all addresses from server
//         setAddresses(updatedAddresses);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
        
//         // Reset form
//         setFormData({
//           fullName: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });

//         toast.success(response.data.msg || "Address saved successfully!");
//       }
//     } catch (err) {
//       console.error("Address save error:", {
//         error: err,
//         response: err.response?.data
//       });

//       const errorMsg = err.response?.data?.msg || 
//                       err.response?.data?.message || 
//                       err.message || 
//                       "Could not save address. Please try again.";
      
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success(`Order placed successfully with ${paymentMethod === "cod" ? "COD" : "online payment"}!`);
//         onClose();
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//       await loadRazorpay();
      
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             const verificationResponse = await axios.post(
//               "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               onClose();
//               clearCart(); 
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     if (!address) return "";
//     return [
//       address.fullName,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   const handleBackToAddress = () => {
//     setStep(1);
//     setShowAddressForm(false);
//     if (step === 2) {
//       setFormData({
//         fullName: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//         step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//       }`}>
//         <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//           <div className="flex justify-between items-center">
//             <h2 className="text-xl font-bold flex items-center gap-2">
//               {step === 1 ? (
//                 showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//               ) : (
//                 <><FiUser /> Payment Method</>
//               )}
//             </h2>
//             <button 
//               onClick={onClose} 
//               className="text-white hover:text-gray-200 text-2xl"
//             >
//               &times;
//             </button>
//           </div>
//         </div>

//         <div className="p-6 overflow-y-auto max-h-[80vh]">
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//               {error}
//             </div>
//           )}

//           {step === 1 ? (
//             showAddressForm ? (
//               <form onSubmit={handleAddressSubmit}>
//                 <div className="space-y-4">
//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Full Name*
//                     </label>
//                     <div className="relative">
//                       <FiUser className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="fullName"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="John Doe"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 1*
//                     </label>
//                     <div className="relative">
//                       <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="addressLine1"
//                         value={formData.addressLine1}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Street address, P.O. box"
//                       />
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 2
//                     </label>
//                     <input
//                       type="text"
//                       name="addressLine2"
//                       value={formData.addressLine2}
//                       onChange={handleChange}
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Apartment, suite, unit, building"
//                     />
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         City*
//                       </label>
//                       <input
//                         type="text"
//                         name="city"
//                         value={formData.city}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="City"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         State*
//                       </label>
//                       <input
//                         type="text"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         ZIP Code*
//                       </label>
//                       <input
//                         type="text"
//                         name="zipCode"
//                         value={formData.zipCode}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Postal code"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         Country*
//                       </label>
//                       <input
//                         type="text"
//                         name="country"
//                         value={formData.country}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Country"
//                       />
//                     </div>
//                   </div>

//                   <div className="flex items-center">
//                     <input
//                       type="checkbox"
//                       id="isDefault"
//                       name="isDefault"
//                       checked={formData.isDefault}
//                       onChange={handleChange}
//                       className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                     />
//                     <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                       Set as default shipping address
//                     </label>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowAddressForm(false)}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     <FiArrowLeft className="mr-1" /> Back
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     {isSubmitting ? "Saving..." : "Save Address"}
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               <div>
//                 <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                   <FiMapPin /> Select Shipping Address
//                 </h3>
                
//                 {isLoadingAddresses ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                   </div>
//                 ) : addresses.length === 0 ? (
//                   <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                     <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                     <p className="text-gray-600 mb-4">No saved addresses found</p>
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                     >
//                       <FiPlus className="mr-1" /> Add New Address
//                     </button>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                       <div className="grid grid-cols-1 gap-3">
//                         {addresses.map(address => (
//                           <div 
//                             key={address._id}
//                             className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                               selectedAddressId === address._id 
//                                 ? 'border-blue-500 border-2 bg-blue-50 shadow-sm' 
//                                 : 'border-gray-200 hover:border-gray-300'
//                             }`}
//                             onClick={() => setSelectedAddressId(address._id)}
//                           >
//                             <div className="flex">
//                               <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                 selectedAddressId === address._id 
//                                   ? 'text-blue-500' 
//                                   : 'text-gray-400'
//                               }`}>
//                                 {selectedAddressId === address._id ? (
//                                   <FiCheck className="text-xl" />
//                                 ) : (
//                                   <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                 )}
//                               </div>
//                               <div className="flex-1 min-w-0">
//                                 {address.fullName && (
//                                   <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                 )}
//                                 <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                   {formatAddress(address)}
//                                 </p>
//                                 {address.isDefault && (
//                                   <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                     Default
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
                    
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <button
//                         onClick={() => setShowAddressForm(true)}
//                         className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                       >
//                         <FiPlus /> Add New Address
//                       </button>
//                       <button
//                         onClick={() => setStep(2)}
//                         className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                           selectedAddressId 
//                             ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90' 
//                             : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                         }`}
//                         disabled={!selectedAddressId}
//                       >
//                         Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             )
//           ) : (
//             <form onSubmit={handlePaymentSubmit}>
//               <div className="space-y-6">
//                 <div>
//                   <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                     <FiUser /> Select Payment Method
//                   </h3>
//                   <div className="space-y-3">
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "cod" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="cod"
//                         checked={paymentMethod === "cod"}
//                         onChange={() => setPaymentMethod("cod")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Cash on Delivery</span>
//                         <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                       </div>
//                     </label>
                    
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "razorpay" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="razorpay"
//                         checked={paymentMethod === "razorpay"}
//                         onChange={() => setPaymentMethod("razorpay")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Pay with Razorpay</span>
//                         <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                       </div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="pt-4 border-t border-gray-200">
//                   <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                   <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal:</span>
//                       <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Shipping:</span>
//                       <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                       <span className="text-gray-800">Total:</span>
//                       <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                 <button
//                   type="button"
//                   onClick={handleBackToAddress}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   <FiArrowLeft /> Back to Address
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? (
//                     <>
//                       <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                       Processing...
//                     </>
//                   ) : (
//                     "Complete Order"
//                   )}
//                 </button>
//               </div>
//             </form>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import { FiHome, FiUser, FiMapPin, FiArrowLeft, FiPlus, FiCheck } from "react-icons/fi";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart, fetchCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const { user } = useSelector(state => state.auth);

//   useEffect(() => {
//     const fetchAddresses = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/buyer/get-shipping-addresses",
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
        
//         // Ensure we have valid addresses in the response
//         const receivedAddresses = response.data?.shippingAddresses || [];
//         const validAddresses = receivedAddresses.filter(addr => addr && addr._id);
        
//         // Sort addresses: default first, then others
//         const sortedAddresses = [...validAddresses].sort((a, b) => 
//           (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)
//         );
        
//         setAddresses(sortedAddresses);
        
//         const defaultAddress = sortedAddresses.find(addr => addr.isDefault);
//         if (defaultAddress) {
//           setSelectedAddressId(defaultAddress._id);
//         }
//       } catch (err) {
//         console.error("Error fetching shipping addresses:", err);
//         toast.error("Failed to load addresses");
//         setAddresses([]); // Reset addresses on error
//       } finally {
//         setIsLoadingAddresses(false);
//       }
//     };

//     if (step === 1) {
//       fetchAddresses();
//     }
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   // const handleAddressSubmit = async (e) => {
//   //   e.preventDefault();
//   //   setError("");
//   //   setIsSubmitting(true);

//   //   const token = Cookies.get("ecom_user_token");
//   //   try {
//   //     const response = await axios.post(
//   //       "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//   //       formData,
//   //       {
//   //         headers: {
//   //           Authorization: `Bearer ${token}`,
//   //           "Content-Type": "application/json"
//   //         }
//   //       }
//   //     );
      
//   //     if (response.status === 201) {
//   //       toast.success("Shipping address saved successfully!");
//   //       const newAddress = response.data.address;
//   //       setAddresses(prev => [newAddress, ...prev]);
//   //       setSelectedAddressId(newAddress._id);
//   //       setShowAddressForm(false);
//   //     } 
//   //   } catch (err) {
//   //     console.error("Error saving shipping address:", err);
//   //     const errorMsg = err.response?.data?.message || "An error occurred while saving the address";
//   //     setError(errorMsg);
//   //     toast.error(errorMsg);
//   //   } finally {
//   //     setIsSubmitting(false);
//   //   }
//   // };

// const handleAddressSubmit = async (e) => {
//   e.preventDefault();
//   setError("");
//   setIsSubmitting(true);

//   const token = Cookies.get("ecom_user_token");
//   try {
//     const response = await axios.post(
//       "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//       formData,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json"
//         },
//         validateStatus: (status) => status >= 200 && status < 300
//       }
//     );

//     // Debugging logs
//     console.log("API Response Status:", response.status);
//     console.log("Response Data:", response.data);

//     if (response.status === 201) {
//       // Handle both response structures:
//       // 1. { address: {...} } 
//       // 2. Direct address object {...}
//       const newAddress = response.data.address || response.data;
      
//       if (!newAddress || !newAddress._id) {
//         console.error("Invalid address data received:", newAddress);
//         throw new Error("Server returned incomplete address data");
//       }

//       // Update state
//       setAddresses(prev => [newAddress, ...prev]);
//       setSelectedAddressId(newAddress._id);
      
//       // Reset form only if everything succeeded
//       setFormData({
//         fullName: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
      
//       setShowAddressForm(false);
//       toast.success("Address saved successfully!");
//     }
//   } catch (err) {
//     // Detailed error logging
//     console.error("Address submission failed:", {
//       error: err,
//       response: err.response?.data,
//       config: err.config
//     });

//     const errorMsg = err.response?.data?.message || 
//                     err.message || 
//                     "Failed to save address. Please try again.";
    
//     setError(errorMsg);
//     toast.error(errorMsg);
//   } finally {
//     setIsSubmitting(false);
//   }
// };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId && !formData.fullName) {
//       setError("Please select or add a shipping address");
//       toast.error("Please select or add a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const isNewAddress = formData.fullName && !selectedAddressId;
      
//       const requestBody = {
//         ...(isNewAddress
//           ? { shippingAddress: formData }
//           : { shippingAddressId: selectedAddressId }),
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success(`Order placed successfully with ${paymentMethod === "cod" ? "COD" : "online payment"}!`);
//         onClose();
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       const isNewAddress = formData.fullName && !selectedAddressId;
      
//       const requestBody = {
//         ...(isNewAddress
//           ? { shippingAddress: formData }
//           : { shippingAddressId: selectedAddressId }),
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//       await loadRazorpay();
      
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             const verificationResponse = await axios.post(
//               "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               onClose();
//               clearCart(); 
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     if (!address) return "";
//     return [
//       address.fullName,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   const handleBackToAddress = () => {
//     setStep(1);
//     setShowAddressForm(false);
//     if (step === 2) {
//       setFormData({
//         fullName: "",
//         addressLine1: "",
//         addressLine2: "",
//         city: "",
//         state: "",
//         zipCode: "",
//         country: "India",
//         isDefault: false
//       });
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//         step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//       }`}>
//         <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//           <div className="flex justify-between items-center">
//             <h2 className="text-xl font-bold flex items-center gap-2">
//               {step === 1 ? (
//                 showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//               ) : (
//                 <><FiUser /> Payment Method</>
//               )}
//             </h2>
//             <button 
//               onClick={onClose} 
//               className="text-white hover:text-gray-200 text-2xl"
//             >
//               &times;
//             </button>
//           </div>
//         </div>

//         <div className="p-6 overflow-y-auto max-h-[80vh]">
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//               {error}
//             </div>
//           )}

//           {step === 1 ? (
//             showAddressForm ? (
//               <form onSubmit={handleAddressSubmit}>
//                 <div className="space-y-4">
//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Full Name*
//                     </label>
//                     <div className="relative">
//                       <FiUser className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="fullName"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="John Doe"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 1*
//                     </label>
//                     <div className="relative">
//                       <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="addressLine1"
//                         value={formData.addressLine1}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Street address, P.O. box"
//                       />
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 2
//                     </label>
//                     <input
//                       type="text"
//                       name="addressLine2"
//                       value={formData.addressLine2}
//                       onChange={handleChange}
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Apartment, suite, unit, building"
//                     />
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         City*
//                       </label>
//                       <input
//                         type="text"
//                         name="city"
//                         value={formData.city}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="City"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         State*
//                       </label>
//                       <input
//                         type="text"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         ZIP Code*
//                       </label>
//                       <input
//                         type="text"
//                         name="zipCode"
//                         value={formData.zipCode}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Postal code"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         Country*
//                       </label>
//                       <input
//                         type="text"
//                         name="country"
//                         value={formData.country}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Country"
//                       />
//                     </div>
//                   </div>

//                   <div className="flex items-center">
//                     <input
//                       type="checkbox"
//                       id="isDefault"
//                       name="isDefault"
//                       checked={formData.isDefault}
//                       onChange={handleChange}
//                       className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                     />
//                     <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                       Set as default shipping address
//                     </label>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowAddressForm(false)}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     <FiArrowLeft className="mr-1" /> Back
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     {isSubmitting ? "Saving..." : "Save Address"}
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               <div>
//                 <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                   <FiMapPin /> Select Shipping Address
//                 </h3>
                
//                 {isLoadingAddresses ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                   </div>
//                 ) : addresses.length === 0 ? (
//                   <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                     <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                     <p className="text-gray-600 mb-4">No saved addresses found</p>
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                     >
//                       <FiPlus className="mr-1" /> Add New Address
//                     </button>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                       <div className="grid grid-cols-1 gap-3">
//                         {addresses
//                           .filter(address => address && address._id)
//                           .map(address => (
//                             <div 
//                               key={address._id}
//                               className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                                 selectedAddressId === address._id 
//                                   ? 'border-blue-500 border-2 bg-blue-50 shadow-sm' 
//                                   : 'border-gray-200 hover:border-gray-300'
//                               }`}
//                               onClick={() => setSelectedAddressId(address._id)}
//                             >
//                               <div className="flex">
//                                 <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                   selectedAddressId === address._id 
//                                     ? 'text-blue-500' 
//                                     : 'text-gray-400'
//                                 }`}>
//                                   {selectedAddressId === address._id ? (
//                                     <FiCheck className="text-xl" />
//                                   ) : (
//                                     <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                   )}
//                                 </div>
//                                 <div className="flex-1 min-w-0">
//                                   {address.fullName && (
//                                     <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                   )}
//                                   <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                     {formatAddress(address)}
//                                   </p>
//                                   {address.isDefault && (
//                                     <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                       Default
//                                     </span>
//                                   )}
//                                 </div>
//                               </div>
//                             </div>
//                           ))
//                         }
//                       </div>
//                     </div>
                    
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <button
//                         onClick={() => setShowAddressForm(true)}
//                         className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                       >
//                         <FiPlus /> Add New Address
//                       </button>
//                       <button
//                         onClick={() => setStep(2)}
//                         className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                           selectedAddressId || formData.fullName
//                             ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90' 
//                             : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                         }`}
//                         disabled={!selectedAddressId && !formData.fullName}
//                       >
//                         Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             )
//           ) : (
//             <form onSubmit={handlePaymentSubmit}>
//               <div className="space-y-6">
//                 <div>
//                   <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                     <FiUser /> Select Payment Method
//                   </h3>
//                   <div className="space-y-3">
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "cod" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="cod"
//                         checked={paymentMethod === "cod"}
//                         onChange={() => setPaymentMethod("cod")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Cash on Delivery</span>
//                         <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                       </div>
//                     </label>
                    
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "razorpay" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="razorpay"
//                         checked={paymentMethod === "razorpay"}
//                         onChange={() => setPaymentMethod("razorpay")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Pay with Razorpay</span>
//                         <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                       </div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="pt-4 border-t border-gray-200">
//                   <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                   <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal:</span>
//                       <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Shipping:</span>
//                       <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                       <span className="text-gray-800">Total:</span>
//                       <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                 <button
//                   type="button"
//                   onClick={handleBackToAddress}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   <FiArrowLeft /> Back to Address
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? (
//                     <>
//                       <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                       Processing...
//                     </>
//                   ) : (
//                     "Complete Order"
//                   )}
//                 </button>
//               </div>
//             </form>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import { FiHome, FiUser, FiMapPin, FiArrowLeft, FiPlus, FiCheck } from "react-icons/fi";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart, fetchCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const { user } = useSelector(state => state.auth);

//   useEffect(() => {
//     const fetchAddresses = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/buyer/get-shipping-addresses",
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
        
//         // Sort addresses: default first, then others
//         const sortedAddresses = [...response.data.shippingAddresses].sort((a, b) => 
//           (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)
//         );
        
//         setAddresses(sortedAddresses);
        
//         const defaultAddress = sortedAddresses.find(addr => addr.isDefault);
//         if (defaultAddress) {
//           setSelectedAddressId(defaultAddress._id);
//         }
//       } catch (err) {
//         console.error("Error fetching shipping addresses:", err);
//         toast.error("Failed to load addresses");
//       } finally {
//         setIsLoadingAddresses(false);
//       }
//     };

//     if (step === 1) {
//       fetchAddresses();
//     }
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
      
      
//         toast.success("Shipping address saved successfully!");
//         const newAddress = response.data.address;
//         setAddresses(prev => [newAddress, ...prev]); // Add new address to the beginning
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
      
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while saving the address";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId && !formData.fullName) {
//       setError("Please select or add a shipping address");
//       toast.error("Please select or add a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       // Check if we're using a newly added address (formData has values) or existing address
//       const isNewAddress = formData.fullName && !selectedAddressId;
      
//       const requestBody = {
//         ...(isNewAddress
//           ? { shippingAddress: formData } // Send full address object for new address
//           : { shippingAddressId: selectedAddressId }), // Send ID for existing address
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success(`Order placed successfully with ${paymentMethod === "cod" ? "COD" : "online payment"}!`);
//         onClose();
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       // Check if we're using a newly added address (formData has values) or existing address
//       const isNewAddress = formData.fullName && !selectedAddressId;
      
//       const requestBody = {
//         ...(isNewAddress
//           ? { shippingAddress: formData } // Send full address object for new address
//           : { shippingAddressId: selectedAddressId }), // Send ID for existing address
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//       await loadRazorpay();
      
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             const verificationResponse = await axios.post(
//               "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               onClose();
//               clearCart(); 
//               fetchCart();
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//               fetchCart();
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart();
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             fetchCart();
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         fetchCart();
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       fetchCart();
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     return [
//       address.fullName,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//         step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//       }`}>
//         <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//           <div className="flex justify-between items-center">
//             <h2 className="text-xl font-bold flex items-center gap-2">
//               {step === 1 ? (
//                 showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//               ) : (
//                 <><FiUser /> Payment Method</>
//               )}
//             </h2>
//             <button 
//               onClick={onClose} 
//               className="text-white hover:text-gray-200 text-2xl"
//             >
//               &times;
//             </button>
//           </div>
//         </div>

//         <div className="p-6 overflow-y-auto max-h-[80vh]">
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//               {error}
//             </div>
//           )}

//           {step === 1 ? (
//             showAddressForm ? (
//               <form onSubmit={handleAddressSubmit}>
//                 <div className="space-y-4">
//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Full Name*
//                     </label>
//                     <div className="relative">
//                       <FiUser className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="fullName"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="John Doe"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 1*
//                     </label>
//                     <div className="relative">
//                       <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="addressLine1"
//                         value={formData.addressLine1}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Street address, P.O. box"
//                       />
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 2
//                     </label>
//                     <input
//                       type="text"
//                       name="addressLine2"
//                       value={formData.addressLine2}
//                       onChange={handleChange}
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Apartment, suite, unit, building"
//                     />
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         City*
//                       </label>
//                       <input
//                         type="text"
//                         name="city"
//                         value={formData.city}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="City"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         State*
//                       </label>
//                       <input
//                         type="text"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         ZIP Code*
//                       </label>
//                       <input
//                         type="text"
//                         name="zipCode"
//                         value={formData.zipCode}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Postal code"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         Country*
//                       </label>
//                       <input
//                         type="text"
//                         name="country"
//                         value={formData.country}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Country"
//                       />
//                     </div>
//                   </div>

//                   <div className="flex items-center">
//                     <input
//                       type="checkbox"
//                       id="isDefault"
//                       name="isDefault"
//                       checked={formData.isDefault}
//                       onChange={handleChange}
//                       className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                     />
//                     <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                       Set as default shipping address
//                     </label>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowAddressForm(false)}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     <FiArrowLeft className="mr-1" /> Back
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     {isSubmitting ? "Saving..." : "Save Address"}
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               <div>
//                 <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                   <FiMapPin /> Select Shipping Address
//                 </h3>
                
//                 {isLoadingAddresses ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                   </div>
//                 ) : addresses.length === 0 ? (
//                   <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                     <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                     <p className="text-gray-600 mb-4">No saved addresses found</p>
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                     >
//                       <FiPlus className="mr-1" /> Add New Address
//                     </button>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                       <div className="grid grid-cols-1 gap-3">
//                         {addresses.map(address => (
//                           <div 
//                             key={address._id}
//                             className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                               selectedAddressId === address._id 
//                                 ? 'border-blue-500 border-2 bg-blue-50 shadow-sm' 
//                                 : 'border-gray-200 hover:border-gray-300'
//                             }`}
//                             onClick={() => setSelectedAddressId(address._id)}
//                           >
//                             <div className="flex">
//                               <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                 selectedAddressId === address._id 
//                                   ? 'text-blue-500' 
//                                   : 'text-gray-400'
//                               }`}>
//                                 {selectedAddressId === address._id ? (
//                                   <FiCheck className="text-xl" />
//                                 ) : (
//                                   <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                 )}
//                               </div>
//                               <div className="flex-1 min-w-0">
//                                 {address.fullName && (
//                                   <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                 )}
//                                 <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                   {formatAddress(address)}
//                                 </p>
//                                 {address.isDefault && (
//                                   <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                     Default
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
                    
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <button
//                         onClick={() => setShowAddressForm(true)}
//                         className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                       >
//                         <FiPlus /> Add New Address
//                       </button>
//                       <button
//                         onClick={() => setStep(2)}
//                         className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                           selectedAddressId || formData.fullName
//                             ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90' 
//                             : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                         }`}
//                         disabled={!selectedAddressId && !formData.fullName}
//                       >
//                         Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             )
//           ) : (
//             <form onSubmit={handlePaymentSubmit}>
//               <div className="space-y-6">
//                 <div>
//                   <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                     <FiUser /> Select Payment Method
//                   </h3>
//                   <div className="space-y-3">
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "cod" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="cod"
//                         checked={paymentMethod === "cod"}
//                         onChange={() => setPaymentMethod("cod")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Cash on Delivery</span>
//                         <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                       </div>
//                     </label>
                    
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "razorpay" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="razorpay"
//                         checked={paymentMethod === "razorpay"}
//                         onChange={() => setPaymentMethod("razorpay")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Pay with Razorpay</span>
//                         <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                       </div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="pt-4 border-t border-gray-200">
//                   <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                   <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal:</span>
//                       <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Shipping:</span>
//                       <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                       <span className="text-gray-800">Total:</span>
//                       <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                 <button
//                   type="button"
//                   onClick={() => {
//                     setStep(1);
//                     setShowAddressForm(false);
//                   }}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   <FiArrowLeft /> Back to Address
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? (
//                     <>
//                       <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                       Processing...
//                     </>
//                   ) : (
//                     "Complete Order"
//                   )}
//                 </button>
//               </div>
//             </form>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import { FiHome, FiUser, FiMapPin, FiArrowLeft, FiPlus, FiCheck } from "react-icons/fi";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart,fetchCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const { user } = useSelector(state => state.auth);

//   // ... existing code ...

// useEffect(() => {
//   const fetchAddresses = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.get(
//         "https://seller-backend-29o9.onrender.com/api/buyer/get-shipping-addresses",
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );
      
//       // Sort addresses: default first, then others
//       const sortedAddresses = [...response.data.shippingAddresses].sort((a, b) => 
//         (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)
//       );
      
//       setAddresses(sortedAddresses);
      
//       const defaultAddress = sortedAddresses.find(addr => addr.isDefault);
//       if (defaultAddress) {
//         setSelectedAddressId(defaultAddress._id);
//       }
//     } catch (err) {
//       console.error("Error fetching shipping addresses:", err);
//       toast.error("Failed to load addresses");
//     } finally {
//       setIsLoadingAddresses(false);
//     }
//   };

//   if (step === 1) {
//     fetchAddresses();
//   }
// }, [step]);

// // ... existing code ...

//   // useEffect(() => {
//   //   const fetchAddresses = async () => {
//   //     const token = Cookies.get("ecom_user_token");
//   //     try {
//   //       const response = await axios.get(
//   //         "https://seller-backend-29o9.onrender.com/api/buyer/get-shipping-addresses",
//   //         {
//   //           headers: {
//   //             Authorization: `Bearer ${token}`
//   //           }
//   //         }
//   //       );
//   //       setAddresses(response.data.shippingAddresses);
        
//   //       const defaultAddress = response.data.shippingAddresses.find(addr => addr.isDefault);
//   //       if (defaultAddress) {
//   //         setSelectedAddressId(defaultAddress._id);
//   //       }
//   //     } catch (err) {
//   //       console.error("Error fetching shipping addresses:", err);
//   //       toast.error("Failed to load addresses");
//   //     } finally {
//   //       setIsLoadingAddresses(false);
//   //     }
//   //   };

//   //   if (step === 1) {
//   //     fetchAddresses();
//   //   }
//   // }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
      
//       if (response.status === 201) {
//         toast.success("Shipping address saved successfully!");
//         const newAddress = response.data.address;
//         setAddresses(prev => [...prev, newAddress]);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
//         setFormData({
//           fullName: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });
//       } 
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while saving the address";
//       // setError(errorMsg);
//       // toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success("Order placed successfully with COD!");
//         onClose();
//         clearCart();
//         fetchCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   // const handleRazorpayPayment = async () => {
//   //   const token = Cookies.get("ecom_user_token");
//   //   const currency = "INR";

//   //   try {
//   //     const requestBody = {
//   //       shippingAddressId: selectedAddressId,
//   //       paymentMethod: {
//   //         type: "razorpay"
//   //       },
//   //       shippingCost: orderSummary.shippingCharges
//   //     };

//   //     const orderResponse = await axios.post(
//   //       "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//   //       requestBody,
//   //       {
//   //         headers: {
//   //           Authorization: `Bearer ${token}`,
//   //           "Content-Type": "application/json"
//   //         }
//   //       }
//   //     );

//   //     const orderId = orderResponse?.data?.order?._id;
//   //     const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//   //     setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//   //     await loadRazorpay();
      
//   //     const options = {
//   //       key: "rzp_test_heVUS0edM8228s",
//   //       amount: orderResponse?.data?.razorpayOrder?.amount,
//   //       currency,
//   //       name: "Uresia",
//   //       description: "Order Payment",
//   //       order_id: razorpayOrderId,
//   //       handler: async function(response) {
//   //         try {
//   //           const verificationResponse = await axios.post(
//   //             "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//   //             {
//   //               orderId,
//   //               razorpayOrderId: response.razorpay_order_id,
//   //               razorpayPaymentId: response.razorpay_payment_id,
//   //               razorpaySignature: response.razorpay_signature
//   //             },
//   //             {
//   //               headers: {
//   //                 Authorization: `Bearer ${token}`,
//   //                 "Content-Type": "application/json"
//   //               }
//   //             }
//   //           );

//   //           if (verificationResponse?.status === 200) {
//   //             onSubmit(verificationResponse.data.order);
//   //             toast.success("Payment successful! Order placed.");
//   //             onClose();
//   //             clearCart(); 
//   //             fetchCart();
//   //           } else {
//   //             const errorMsg = verificationResponse.data.message || "Payment verification failed";
//   //             setError(errorMsg);
//   //             toast.error(errorMsg);
//   //           }
//   //         } catch (err) {
//   //           console.error("Error verifying payment:", err);
//   //           const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//   //           setError(errorMsg);
//   //           toast.error(errorMsg);
//   //         } finally {
//   //           setIsSubmitting(false);
//   //         }
//   //       },
//   //       prefill: {
//   //         name: user?.name || "",
//   //         email: user?.email || "",
//   //         contact: user?.phone || "9999999999"
//   //       },
//   //       theme: {
//   //         color: "#3399cc"
//   //       },
//   //       modal: {
//   //         ondismiss: function() {
//   //           toast.info("Payment window closed");
//   //           setIsSubmitting(false);
//   //         }
//   //       }
//   //     };

//   //     const rzp = new window.Razorpay(options);
//   //     rzp.on('payment.failed', function(response) {
//   //       toast.error(`Payment failed: ${response.error.description}`);
//   //       setIsSubmitting(false);
//   //     });
//   //     rzp.open();
//   //   } catch (err) {
//   //     console.error("Error in Razorpay payment:", err);
//   //     const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//   //     setError(errorMsg);
//   //     toast.error(errorMsg);
//   //     setIsSubmitting(false);
//   //   }
//   // };

//   const handleRazorpayPayment = async () => {
//   const token = Cookies.get("ecom_user_token");
//   const currency = "INR";

//   try {
//     const requestBody = {
//       shippingAddressId: selectedAddressId,
//       paymentMethod: {
//         type: "razorpay"
//       },
//       shippingCost: orderSummary.shippingCharges
//     };

//     const orderResponse = await axios.post(
//       "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//       requestBody,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json"
//         }
//       }
//     );

//     const orderId = orderResponse?.data?.order?._id;
//     const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//     setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//     await loadRazorpay();
    
//     const options = {
//       key: "rzp_test_heVUS0edM8228s",
//       amount: orderResponse?.data?.razorpayOrder?.amount,
//       currency,
//       name: "Uresia",
//       description: "Order Payment",
//       order_id: razorpayOrderId,
//       handler: async function(response) {
//         try {
//           const verificationResponse = await axios.post(
//             "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//             {
//               orderId,
//               razorpayOrderId: response.razorpay_order_id,
//               razorpayPaymentId: response.razorpay_payment_id,
//               razorpaySignature: response.razorpay_signature
//             },
//             {
//               headers: {
//                 Authorization: `Bearer ${token}`,
//                 "Content-Type": "application/json"
//               }
//             }
//           );

//           if (verificationResponse?.status === 200) {
//             onSubmit(verificationResponse.data.order);
//             toast.success("Payment successful! Order placed.");
//             onClose();
//             clearCart(); 
//             fetchCart();
//           } else {
//             const errorMsg = verificationResponse.data.message || "Payment verification failed";
//             setError(errorMsg);
//             toast.error(errorMsg);
//             fetchCart(); // Refresh cart on verification failure
//           }
//         } catch (err) {
//           console.error("Error verifying payment:", err);
//           const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//           setError(errorMsg);
//           toast.error(errorMsg);
//           fetchCart(); // Refresh cart on error
//         } finally {
//           setIsSubmitting(false);
//         }
//       },
//       prefill: {
//         name: user?.name || "",
//         email: user?.email || "",
//         contact: user?.phone || "9999999999"
//       },
//       theme: {
//         color: "#3399cc"
//       },
//       modal: {
//         ondismiss: function() {
//           toast.info("Payment window closed");
//           fetchCart(); // Refresh cart when modal is dismissed
//           setIsSubmitting(false);
//         }
//       }
//     };

//     const rzp = new window.Razorpay(options);
//     rzp.on('payment.failed', function(response) {
//       toast.error(`Payment failed: ${response.error.description}`);
//       fetchCart(); // Refresh cart on payment failure
//       setIsSubmitting(false);
//     });
//     rzp.open();
//   } catch (err) {
//     console.error("Error in Razorpay payment:", err);
//     const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//     setError(errorMsg);
//     toast.error(errorMsg);
//     fetchCart(); // Refresh cart on error
//     setIsSubmitting(false);
//   }
// };

//   const formatAddress = (address) => {
//     return [
//       address.fullName,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className={`bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden ${
//         step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//       }`}>
//         <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//           <div className="flex justify-between items-center">
//             <h2 className="text-xl font-bold flex items-center gap-2">
//               {step === 1 ? (
//                 showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//               ) : (
//                 <><FiUser /> Payment Method</>
//               )}
//             </h2>
//             <button 
//               onClick={onClose} 
//               className="text-white hover:text-gray-200 text-2xl"
//             >
//               &times;
//             </button>
//           </div>
//         </div>

//         <div className="p-6 overflow-y-auto max-h-[80vh]">
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//               {error}
//             </div>
//           )}

//           {step === 1 ? (
//             showAddressForm ? (
//               <form onSubmit={handleAddressSubmit}>
//                 <div className="space-y-4">
//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Full Name*
//                     </label>
//                     <div className="relative">
//                       <FiUser className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="fullName"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="John Doe"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 1*
//                     </label>
//                     <div className="relative">
//                       <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="addressLine1"
//                         value={formData.addressLine1}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Street address, P.O. box"
//                       />
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 2
//                     </label>
//                     <input
//                       type="text"
//                       name="addressLine2"
//                       value={formData.addressLine2}
//                       onChange={handleChange}
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Apartment, suite, unit, building"
//                     />
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         City*
//                       </label>
//                       <input
//                         type="text"
//                         name="city"
//                         value={formData.city}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="City"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         State*
//                       </label>
//                       <input
//                         type="text"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         ZIP Code*
//                       </label>
//                       <input
//                         type="text"
//                         name="zipCode"
//                         value={formData.zipCode}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Postal code"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         Country*
//                       </label>
//                       <input
//                         type="text"
//                         name="country"
//                         value={formData.country}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Country"
//                       />
//                     </div>
//                   </div>

//                   <div className="flex items-center">
//                     <input
//                       type="checkbox"
//                       id="isDefault"
//                       name="isDefault"
//                       checked={formData.isDefault}
//                       onChange={handleChange}
//                       className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                     />
//                     <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                       Set as default shipping address
//                     </label>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowAddressForm(false)}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     <FiArrowLeft className="mr-1" /> Back
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     {isSubmitting ? "Saving..." : "Save Address"}
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               <div>
//                 <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                   <FiMapPin /> Select Shipping Address
//                 </h3>
                
//                 {isLoadingAddresses ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                   </div>
//                 ) : addresses.length === 0 ? (
//                   <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                     <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                     <p className="text-gray-600 mb-4">No saved addresses found</p>
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                     >
//                       <FiPlus className="mr-1" /> Add New Address
//                     </button>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                       <div className="grid grid-cols-1 gap-3">
//                         {addresses.map(address => (
//                           <div 
//                             key={address._id}
//                             className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                               selectedAddressId === address._id 
//                                 ? 'border-blue-500 border-2 bg-blue-50 shadow-sm' 
//                                 : 'border-gray-200 hover:border-gray-300'
//                             }`}
//                             onClick={() => setSelectedAddressId(address._id)}
//                           >
//                             <div className="flex">
//                               <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                 selectedAddressId === address._id 
//                                   ? 'text-blue-500' 
//                                   : 'text-gray-400'
//                               }`}>
//                                 {selectedAddressId === address._id ? (
//                                   <FiCheck className="text-xl" />
//                                 ) : (
//                                   <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                 )}
//                               </div>
//                               <div className="flex-1 min-w-0">
//                                 {address.fullName && (
//                                   <p className="font-medium text-gray-800 truncate">{address.fullName}</p>
//                                 )}
//                                 <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">
//                                   {formatAddress(address)}
//                                 </p>
//                                 {address.isDefault && (
//                                   <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                     Default
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
                    
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <button
//                         onClick={() => setShowAddressForm(true)}
//                         className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                       >
//                         <FiPlus /> Add New Address
//                       </button>
//                       <button
//                         onClick={() => setStep(2)}
//                         className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                           selectedAddressId 
//                             ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90' 
//                             : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                         }`}
//                         disabled={!selectedAddressId}
//                       >
//                         Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             )
//           ) : (
//             <form onSubmit={handlePaymentSubmit}>
//               <div className="space-y-6">
//                 <div>
//                   <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                     <FiUser /> Select Payment Method
//                   </h3>
//                   <div className="space-y-3">
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "cod" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="cod"
//                         checked={paymentMethod === "cod"}
//                         onChange={() => setPaymentMethod("cod")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Cash on Delivery</span>
//                         <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                       </div>
//                     </label>
                    
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "razorpay" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="razorpay"
//                         checked={paymentMethod === "razorpay"}
//                         onChange={() => setPaymentMethod("razorpay")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Pay with Razorpay</span>
//                         <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                       </div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="pt-4 border-t border-gray-200">
//                   <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                   <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal:</span>
//                       <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Shipping:</span>
//                       <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                       <span className="text-gray-800">Total:</span>
//                       <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                 <button
//                   type="button"
//                   onClick={() => {
//                     setStep(1);
//                     setShowAddressForm(false);
//                   }}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   <FiArrowLeft /> Back to Address
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? (
//                     <>
//                       <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                       Processing...
//                     </>
//                   ) : (
//                     "Complete Order"
//                   )}
//                 </button>
//               </div>
//             </form>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import { FiHome, FiUser, FiMapPin, FiArrowLeft, FiPlus, FiCheck } from "react-icons/fi";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "India",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const { user } = useSelector(state => state.auth);

//   useEffect(() => {
//     const fetchAddresses = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/buyer/get-shipping-addresses",
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setAddresses(response.data.shippingAddresses);
        
//         // Set the default address if exists
//         const defaultAddress = response.data.shippingAddresses.find(addr => addr.isDefault);
//         if (defaultAddress) {
//           setSelectedAddressId(defaultAddress._id);
//         }
//       } catch (err) {
//         console.error("Error fetching shipping addresses:", err);
//         toast.error("Failed to load addresses");
//       } finally {
//         setIsLoadingAddresses(false);
//       }
//     };

//     if (step === 1) {
//       fetchAddresses();
//     }
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
      
//       if (response.status === 201) {
//         toast.success("Shipping address saved successfully!");
//         // Add new address to the list and select it
//         const newAddress = response.data.address;
//         setAddresses(prev => [...prev, newAddress]);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
//         // Reset form
//         setFormData({
//           fullName: "",
//           addressLine1: "",
//           addressLine2: "",
//           city: "",
//           state: "",
//           zipCode: "",
//           country: "India",
//           isDefault: false
//         });
//       } else {
//         setError(response.data.message || "Failed to save address");
//         toast.error(response.data.message || "Failed to save address");
//       }
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while saving the address";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     // Validate address selection
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success("Order placed successfully with COD!");
//         onClose();
//         clearCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//       await loadRazorpay();
      
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             const verificationResponse = await axios.post(
//               "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               onClose();
//               clearCart(); 
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     return [
//       address.fullName,
//       address.addressLine1,
//       address.addressLine2,
//       `${address.city}, ${address.state} ${address.zipCode}`,
//       address.country
//     ].filter(Boolean).join(", ");
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className={`bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden ${
//         step === 1 && !showAddressForm ? 'max-h-[90vh]' : ''
//       }`}>
//         <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
//           <div className="flex justify-between items-center">
//             <h2 className="text-xl font-bold flex items-center gap-2">
//               {step === 1 ? (
//                 showAddressForm ? <><FiMapPin /> Add Address</> : <><FiHome /> Shipping Address</>
//               ) : (
//                 <><FiUser /> Payment Method</>
//               )}
//             </h2>
//             <button 
//               onClick={onClose} 
//               className="text-white hover:text-gray-200 text-2xl"
//             >
//               &times;
//             </button>
//           </div>
//         </div>

//         <div className="p-4 overflow-y-auto max-h-[80vh]">
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
//               {error}
//             </div>
//           )}

//           {step === 1 ? (
//             showAddressForm ? (
//               <form onSubmit={handleAddressSubmit}>
//                 <div className="space-y-4">
//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Full Name*
//                     </label>
//                     <div className="relative">
//                       <FiUser className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="fullName"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="John Doe"
//                       />
//                     </div>
//                   </div>

//                   <div className="relative">
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 1*
//                     </label>
//                     <div className="relative">
//                       <FiMapPin className="absolute left-3 top-3 text-gray-400" />
//                       <input
//                         type="text"
//                         name="addressLine1"
//                         value={formData.addressLine1}
//                         onChange={handleChange}
//                         required
//                         className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Street address, P.O. box"
//                       />
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                       Address Line 2
//                     </label>
//                     <input
//                       type="text"
//                       name="addressLine2"
//                       value={formData.addressLine2}
//                       onChange={handleChange}
//                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                       placeholder="Apartment, suite, unit, building"
//                     />
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         City*
//                       </label>
//                       <input
//                         type="text"
//                         name="city"
//                         value={formData.city}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="City"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         State*
//                       </label>
//                       <input
//                         type="text"
//                         name="state"
//                         value={formData.state}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="State"
//                       />
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         ZIP Code*
//                       </label>
//                       <input
//                         type="text"
//                         name="zipCode"
//                         value={formData.zipCode}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Postal code"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-1 pl-1">
//                         Country*
//                       </label>
//                       <input
//                         type="text"
//                         name="country"
//                         value={formData.country}
//                         onChange={handleChange}
//                         required
//                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         placeholder="Country"
//                       />
//                     </div>
//                   </div>

//                   <div className="flex items-center">
//                     <input
//                       type="checkbox"
//                       id="isDefault"
//                       name="isDefault"
//                       checked={formData.isDefault}
//                       onChange={handleChange}
//                       className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                     />
//                     <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                       Set as default shipping address
//                     </label>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowAddressForm(false)}
//                     className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     <FiArrowLeft className="mr-1" /> Back
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
//                     disabled={isSubmitting}
//                   >
//                     {isSubmitting ? "Saving..." : "Save Address"}
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               <div>
//                 <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
//                   <FiMapPin /> Select Shipping Address
//                 </h3>
                
//                 {isLoadingAddresses ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
//                   </div>
//                 ) : addresses.length === 0 ? (
//                   <div className="text-center py-8 border-2 border-dashed rounded-xl mb-4">
//                     <FiMapPin className="mx-auto text-3xl text-gray-400 mb-3" />
//                     <p className="text-gray-600 mb-4">No saved addresses found</p>
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center mx-auto"
//                     >
//                       <FiPlus className="mr-1" /> Add New Address
//                     </button>
//                   </div>
//                 ) : (
//                   <>
//                     <div className="mb-4 max-h-[50vh] overflow-y-auto pr-2">
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
//                         {addresses.map(address => (
//                           <div 
//                             key={address._id}
//                             className={`p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
//                               selectedAddressId === address._id 
//                                 ? 'border-blue-500 border-2 bg-blue-50 shadow-sm' 
//                                 : 'border-gray-200 hover:border-gray-300'
//                             }`}
//                             onClick={() => setSelectedAddressId(address._id)}
//                           >
//                             <div className="flex">
//                               <div className={`mr-3 mt-1 flex-shrink-0 ${
//                                 selectedAddressId === address._id 
//                                   ? 'text-blue-500' 
//                                   : 'text-gray-400'
//                               }`}>
//                                 {selectedAddressId === address._id ? (
//                                   <FiCheck className="text-xl" />
//                                 ) : (
//                                   <div className="w-5 h-5 rounded-full border border-gray-300"></div>
//                                 )}
//                               </div>
//                               <div className="flex-1">
//                                 {address.fullName && (
//                                   <p className="font-medium text-gray-800">{address.fullName}</p>
//                                 )}
//                                 <p className="text-sm text-gray-600 mt-1">
//                                   {formatAddress(address)}
//                                 </p>
//                                 {address.isDefault && (
//                                   <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
//                                     Default
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
                    
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <button
//                         onClick={() => setShowAddressForm(true)}
//                         className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 flex-1"
//                       >
//                         <FiPlus /> Add New Address
//                       </button>
//                       <button
//                         onClick={() => setStep(2)}
//                         className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 flex-1 ${
//                           selectedAddressId 
//                             ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:opacity-90' 
//                             : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                         }`}
//                         disabled={!selectedAddressId}
//                       >
//                         Continue to Payment <FiArrowLeft className="transform rotate-180" />
//                       </button>
//                     </div>
//                   </>
//                 )}
//               </div>
//             )
//           ) : (
//             <form onSubmit={handlePaymentSubmit}>
//               <div className="space-y-6">
//                 <div>
//                   <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
//                     <FiUser /> Select Payment Method
//                   </h3>
//                   <div className="space-y-3">
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "cod" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="cod"
//                         checked={paymentMethod === "cod"}
//                         onChange={() => setPaymentMethod("cod")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Cash on Delivery</span>
//                         <span className="block text-sm text-gray-500 mt-1">Pay when you receive your order</span>
//                       </div>
//                     </label>
                    
//                     <label className={`flex items-center p-4 border rounded-xl transition-all ${
//                       paymentMethod === "razorpay" 
//                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
//                         : 'border-gray-200 hover:border-blue-300'
//                     }`}>
//                       <input
//                         type="radio"
//                         name="paymentMethod"
//                         value="razorpay"
//                         checked={paymentMethod === "razorpay"}
//                         onChange={() => setPaymentMethod("razorpay")}
//                         className="h-5 w-5 text-blue-600 focus:ring-blue-500"
//                       />
//                       <div className="ml-3">
//                         <span className="block font-medium">Pay with Razorpay</span>
//                         <span className="block text-sm text-gray-500 mt-1">Secure online payment</span>
//                       </div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="pt-4 border-t border-gray-200">
//                   <h3 className="text-lg font-medium mb-3">Order Summary</h3>
//                   <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Subtotal:</span>
//                       <span className="font-medium">₹{orderSummary.subtotal?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-600">Shipping:</span>
//                       <span className="font-medium">₹{orderSummary.shippingCharges?.toFixed(2)}</span>
//                     </div>
//                     <div className="flex justify-between font-medium pt-3 mt-2 border-t border-gray-200">
//                       <span className="text-gray-800">Total:</span>
//                       <span className="text-lg text-blue-600">₹{orderSummary.total?.toFixed(2)}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 flex flex-col-reverse sm:flex-row justify-between gap-3">
//                 <button
//                   type="button"
//                   onClick={() => {
//                     setStep(1);
//                     setShowAddressForm(false);
//                   }}
//                   className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   <FiArrowLeft /> Back to Address
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? (
//                     <>
//                       <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
//                       Processing...
//                     </>
//                   ) : (
//                     "Complete Order"
//                   )}
//                 </button>
//               </div>
//             </form>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems, clearCart }) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount, setAmount] = useState(0);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1);
//   const [addresses, setAddresses] = useState([]);
//   const [selectedAddressId, setSelectedAddressId] = useState("");
//   const [showAddressForm, setShowAddressForm] = useState(false);
//   const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

//   const { user } = useSelector(state => state.auth);

//   // Fetch existing shipping addresses
//   useEffect(() => {
//     const fetchAddresses = async () => {
//       const token = Cookies.get("ecom_user_token");
//       try {
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/buyer/get-shipping-addresses",
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//         setAddresses(response.data.shippingAddresses);
        
//         // Set the default address if exists
//         const defaultAddress = response.data.shippingAddresses.find(addr => addr.isDefault);
//         if (defaultAddress) {
//           setSelectedAddressId(defaultAddress._id);
//         }
//       } catch (err) {
//         console.error("Error fetching shipping addresses:", err);
//         toast.error("Failed to load addresses");
//       } finally {
//         setIsLoadingAddresses(false);
//       }
//     };

//     if (step === 1) {
//       fetchAddresses();
//     }
//   }, [step]);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
      
//       if (response.status === 201) {
//         toast.success("Shipping address saved successfully!");
//         // Add new address to the list and select it
//         const newAddress = response.data.address;
//         setAddresses(prev => [...prev, newAddress]);
//         setSelectedAddressId(newAddress._id);
//         setShowAddressForm(false);
//       } else {
//         setError(response.data.message || "Failed to save address");
//         toast.error(response.data.message || "Failed to save address");
//       }
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while saving the address";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     // Validate address selection
//     if (!selectedAddressId) {
//       setError("Please select a shipping address");
//       toast.error("Please select a shipping address");
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success("Order placed successfully with COD!");
//         onClose();
//         clearCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       const requestBody = {
//         shippingAddressId: selectedAddressId,
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100);

//       await loadRazorpay();
      
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             const verificationResponse = await axios.post(
//               "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               onClose();
//               clearCart(); 
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const formatAddress = (address) => {
//     return `
//       ${address.fullName ? address.fullName + ', ' : ''}
//       ${address.addressLine1}, 
//       ${address.addressLine2 ? address.addressLine2 + ', ' : ''}
//       ${address.city}, ${address.state} ${address.zipCode}, 
//       ${address.country}
//     `;
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg p-6 w-full max-w-md">
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-bold">
//             {step === 1 ? "Shipping Address" : "Payment Method"}
//           </h2>
//           <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//             &times;
//           </button>
//         </div>

//         {error && (
//           <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
//             {error}
//           </div>
//         )}

//         {step === 1 ? (
//           showAddressForm ? (
//             <form onSubmit={handleAddressSubmit}>
//               <div className="space-y-4">
//                 {/* Address form fields (same as before) */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Full Name*
//                   </label>
//                   <input
//                     type="text"
//                     name="fullName"
//                     value={formData.fullName}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Address Line 1*
//                   </label>
//                   <input
//                     type="text"
//                     name="addressLine1"
//                     value={formData.addressLine1}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Address Line 2
//                   </label>
//                   <input
//                     type="text"
//                     name="addressLine2"
//                     value={formData.addressLine2}
//                     onChange={handleChange}
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>

//                 <div className="grid grid-cols-2 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       City*
//                     </label>
//                     <input
//                       type="text"
//                       name="city"
//                       value={formData.city}
//                       onChange={handleChange}
//                       required
//                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       State*
//                     </label>
//                     <input
//                       type="text"
//                       name="state"
//                       value={formData.state}
//                       onChange={handleChange}
//                       required
//                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                   </div>
//                 </div>

//                 <div className="grid grid-cols-2 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       ZIP Code*
//                     </label>
//                     <input
//                       type="text"
//                       name="zipCode"
//                       value={formData.zipCode}
//                       onChange={handleChange}
//                       required
//                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       Country*
//                     </label>
//                     <input
//                       type="text"
//                       name="country"
//                       value={formData.country}
//                       onChange={handleChange}
//                       required
//                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                   </div>
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     type="checkbox"
//                     id="isDefault"
//                     name="isDefault"
//                     checked={formData.isDefault}
//                     onChange={handleChange}
//                     className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                   />
//                   <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                     Set as default shipping address
//                   </label>
//                 </div>
//               </div>

//               <div className="mt-6 flex justify-end space-x-3">
//                 <button
//                   type="button"
//                   onClick={() => setShowAddressForm(false)}
//                   className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                   disabled={isSubmitting}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? "Saving..." : "Save Address"}
//                 </button>
//               </div>
//             </form>
//           ) : (
//             <div>
//               <h3 className="text-lg font-medium mb-4">Select Shipping Address</h3>
              
//               {isLoadingAddresses ? (
//                 <p>Loading addresses...</p>
//               ) : addresses.length === 0 ? (
//                 <div className="mb-4">
//                   <p className="text-gray-600 mb-3">No saved addresses found.</p>
//                   <button
//                     onClick={() => setShowAddressForm(true)}
//                     className="w-full py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//                   >
//                     Add New Address
//                   </button>
//                 </div>
//               ) : (
//                 <>
//                   <div className="max-h-60 overflow-y-auto mb-4 border rounded-md">
//                     {addresses.map(address => (
//                       <div 
//                         key={address._id}
//                         className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
//                           selectedAddressId === address._id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
//                         }`}
//                         onClick={() => setSelectedAddressId(address._id)}
//                       >
//                         <div className="flex items-start">
//                           <input
//                             type="radio"
//                             name="selectedAddress"
//                             checked={selectedAddressId === address._id}
//                             onChange={() => {}}
//                             className="mt-1 mr-2"
//                           />
//                           <div>
//                             {address.fullName && (
//                               <p className="font-medium">{address.fullName}</p>
//                             )}
//                             <p className="text-sm">
//                               {address.addressLine1}
//                               {address.addressLine2 && `, ${address.addressLine2}`}
//                             </p>
//                             <p className="text-sm">
//                               {address.city}, {address.state} {address.zipCode}
//                             </p>
//                             <p className="text-sm">{address.country}</p>
//                             {address.isDefault && (
//                               <span className="inline-block mt-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
//                                 Default
//                               </span>
//                             )}
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
                  
//                   <div className="flex flex-col sm:flex-row gap-3">
//                     <button
//                       onClick={() => setShowAddressForm(true)}
//                       className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
//                     >
//                       Add New Address
//                     </button>
//                     <button
//                       onClick={() => setStep(2)}
//                       className={`px-4 py-2 rounded-md ${
//                         selectedAddressId 
//                           ? 'bg-blue-500 text-white hover:bg-blue-600' 
//                           : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                       }`}
//                       disabled={!selectedAddressId}
//                     >
//                       Continue to Payment
//                     </button>
//                   </div>
//                 </>
//               )}
//             </div>
//           )
//         ) : (
//           <form onSubmit={handlePaymentSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <h3 className="text-lg font-medium mb-2">Select Payment Method</h3>
//                 <div className="space-y-2">
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="cod"
//                       checked={paymentMethod === "cod"}
//                       onChange={() => setPaymentMethod("cod")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Cash on Delivery</span>
//                   </label>
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="razorpay"
//                       checked={paymentMethod === "razorpay"}
//                       onChange={() => setPaymentMethod("razorpay")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Pay with Razorpay</span>
//                   </label>
//                 </div>
//               </div>

//               <div className="pt-4 border-t border-gray-200">
//                 <h3 className="text-lg font-medium mb-2">Order Summary</h3>
//                 <div className="space-y-1 text-sm">
//                   <div className="flex justify-between">
//                     <span>Subtotal:</span>
//                     <span>{orderSummary.subtotal?.toFixed(2)} Rs</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span>Shipping:</span>
//                     <span>{orderSummary.shippingCharges?.toFixed(2)} Rs</span>
//                   </div>
//                   <div className="flex justify-between font-medium pt-2 border-t border-gray-200">
//                     <span>Total:</span>
//                     <span>{orderSummary.total?.toFixed(2)} Rs</span>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="mt-6 flex justify-between space-x-3">
//               <button
//                 type="button"
//                 onClick={() => {
//                   setStep(1);
//                   setShowAddressForm(false);
//                 }}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                 disabled={isSubmitting}
//               >
//                 Back
//               </button>
//               <button
//                 type="submit"
//                 className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "Processing..." : "Complete Order"}
//               </button>
//             </div>
//           </form>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems,clearCart}) => {
//   const [formData, setFormData] = useState({
//     fullName: "",
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [amount,setAmount] = useState(0)
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1); // 1: Address, 2: Payment
// // const [total, setTotal] = useState(orderSummary?.total*100
// // );
//   const { user } = useSelector(state => state.auth);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   // const handleAddressSubmit = async (e) => {
//   //   // console.log("total round",Math.round(orderSummary.total * 100))
//   //   // console.log("total", total, typeof total);
//   //   // console.log("order summary",orderSummary)
//   //   // console.log("orderSummary.total:", orderSummary.total, "Type:", typeof orderSummary.total);

//   //   e.preventDefault();
//   //   setError("");
//   //   setIsSubmitting(true);

//   //   const token = Cookies.get("ecom_user_token");
//   //   try {
//   //     const response = await axios.post(
//   //       "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//   //       formData,
//   //       {
//   //         headers: {
//   //           Authorization: `Bearer ${token}`,
//   //           "Content-Type": "application/json"
//   //         }
//   //       }
//   //     );
      
//   //     if (response.status === 201) {
//   //       setStep(2);
//   //     } else {
//   //       setError(response.data.message || "Failed to save address");
//   //     }
//   //   } catch (err) {
//   //     console.error("Error saving shipping address:", err);
//   //     setError(err.response?.data?.message || "An error occurred while saving the address");
//   //   } finally {
//   //     setIsSubmitting(false);
//   //   }
//   // };

//   // const handlePaymentSubmit = async (e) => {
//   //   e.preventDefault();
//   //   setIsSubmitting(true);
//   //   setError("");
    
//   //   try {
//   //     if (paymentMethod === "cod") {
//   //       await handlePlaceOrder();
//   //     } else {
//   //       await handleRazorpayPayment();
//   //     }
//   //   } catch (err) {
//   //     console.error("Payment error:", err);
//   //     setError(err.message || "An error occurred during payment processing");
//   //     setIsSubmitting(false);
//   //   }
//   // };

//   // const handlePlaceOrder = async () => {
//   //   const token = Cookies.get("ecom_user_token");
//   //   const requestBody = {
//   //     shippingAddress: {
//   //       fullName: formData.fullName,
//   //       addressLine1: formData.addressLine1,
//   //       addressLine2: formData.addressLine2,
//   //       city: formData.city,
//   //       state: formData.state,
//   //       zipCode: formData.zipCode,
//   //       country: formData.country,
//   //       isDefault: formData.isDefault
//   //     },
//   //     paymentMethod: {
//   //       type: paymentMethod
//   //     },
//   //     shippingCost: orderSummary.shippingCharges
//   //   };

//   //   const response = await axios.post(
//   //     "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//   //     requestBody,
//   //     {
//   //       headers: {
//   //         Authorization: `Bearer ${token}`,
//   //         "Content-Type": "application/json"
//   //       }
//   //     }
//   //   );

//   //   if (response.status === 201) {
//   //     setAmount(response?.data?.razorpayOrder?.amount)
//   //     onSubmit(response.data.order);
//   //     onClose();
//   //     clearCart();
//   //   } else {
//   //     throw new Error(response.data.message || "Failed to place order");
//   //   }
//   // };

//   // const handleRazorpayPayment = async () => {
//   //   const token = Cookies.get("ecom_user_token");
    
//   //   // 1. Create order in your backend
//   //   const requestBody = {
//   //     shippingAddress: {
//   //       fullName: formData.fullName,
//   //       addressLine1: formData.addressLine1,
//   //       addressLine2: formData.addressLine2,
//   //       city: formData.city,
//   //       state: formData.state,
//   //       zipCode: formData.zipCode,
//   //       country: formData.country,
//   //       isDefault: formData.isDefault
//   //     },
//   //     paymentMethod: {
//   //       type: "razorpay"
//   //     },
//   //     shippingCost: orderSummary.shippingCharges
//   //   };

//   //   const orderResponse = await axios.post(
//   //     "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//   //     requestBody,
//   //     {
//   //       headers: {
//   //         Authorization: `Bearer ${token}`,
//   //         "Content-Type": "application/json"
//   //       }
//   //     }
//   //   );

//   //   // if (!orderResponse.data.success) {
//   //   //   throw new Error(orderResponse.data.message || "Failed to create order");
//   //   // }

//   //   const orderId = orderResponse?.data?.order?._id;
//   //   const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id

//   //   // 2. Load Razorpay script
//   //   await loadRazorpay();
//   //    console.log("order total", total)
//   //   // 3. Initialize Razorpay payment
//   //   const options = {
//   //     key: "rzp_test_heVUS0edM8228s",
//   //     amount: Math.round(orderSummary.total * 100),
//   //     currency: "INR",
//   //     name: "Uresia",
//   //     description: "Order Payment",
//   //     order_id: razorpayOrderId,
//   //     handler: async function(response) {
//   //       try {
//   //         // Verify payment with your backend
//   //         console.log("razorpay", response)
//   //         const verificationResponse = await axios.post(
//   //           "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//   //           {
//   //             orderId,
//   //             razorpayOrderId: response.razorpay_order_id,
//   //             razorpayPaymentId: response.razorpay_payment_id,
//   //             razorpaySignature: response.razorpay_signature
//   //           },
//   //           {
//   //             headers: {
//   //               Authorization: `Bearer ${token}`,
//   //               "Content-Type": "application/json"
//   //             }
//   //           }
//   //         );

//   //         if (verificationResponse?.status===200) {
//   //           onSubmit(verificationResponse.data.order);
//   //           onClose();
//   //           clearCart(); 
//   //         } else {
//   //           setError(verificationResponse.data.message || "Payment verification failed");
//   //         }
//   //       } catch (err) {
//   //         console.error("Error verifying payment:", err);
//   //         setError(err.response?.data?.message || "An error occurred while verifying payment");
//   //       } finally {
//   //         setIsSubmitting(false);
//   //       }
//   //     },
//   //     prefill: {
//   //       name: user?.name || "",
//   //       email: user?.email || "",
//   //       contact: user?.phone || "9999999999"
//   //     },
//   //     theme: {
//   //       color: "#3399cc"
//   //     }
//   //   };

//   //   const rzp = new window.Razorpay(options);
//   //   rzp.open();
//   // };
// //   const handleRazorpayPayment = async () => {
// //   const token = Cookies.get("ecom_user_token");
  
// //   // Create constants for amount and currency
// //   // const amount = Math.round(orderSummary.total * 100); // Convert to paise
// //   const currency = "INR";

// //   // 1. Create order in your backend
// //   const requestBody = {
// //     shippingAddress: {
// //       fullName: formData.fullName,
// //       addressLine1: formData.addressLine1,
// //       addressLine2: formData.addressLine2,
// //       city: formData.city,
// //       state: formData.state,
// //       zipCode: formData.zipCode,
// //       country: formData.country,
// //       isDefault: formData.isDefault
// //     },
// //     paymentMethod: {
// //       type: "razorpay"
// //     },
// //     shippingCost: orderSummary.shippingCharges
// //   };

// //   const orderResponse = await axios.post(
// //     "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
// //     requestBody,
// //     {
// //       headers: {
// //         Authorization: `Bearer ${token}`,
// //         "Content-Type": "application/json"
// //       }
// //     }
// //   );

// //   const orderId = orderResponse?.data?.order?._id;
// //   const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;

// //   // 2. Load Razorpay script
// //   await loadRazorpay();
  
// //   // 3. Initialize Razorpay payment
// //   const options = {
// //     key: "rzp_test_heVUS0edM8228s",
// //     amount:Math.round(amount * 100),
// //     currency, // Use the constant here
// //     name: "Uresia",
// //     description: "Order Payment",
// //     order_id: razorpayOrderId,
// //     handler: async function(response) {
// //       try {
// //         // Verify payment with your backend
// //         const verificationResponse = await axios.post(
// //           "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
// //           {
// //             orderId,
// //             razorpayOrderId: response.razorpay_order_id,
// //             razorpayPaymentId: response.razorpay_payment_id,
// //             razorpaySignature: response.razorpay_signature
// //           },
// //           {
// //             headers: {
// //               Authorization: `Bearer ${token}`,
// //               "Content-Type": "application/json"
// //             }
// //           }
// //         );

// //         if (verificationResponse?.status===200) {
// //           onSubmit(verificationResponse.data.order);
// //           onClose();
// //           clearCart(); 
// //         } else {
// //           setError(verificationResponse.data.message || "Payment verification failed");
// //         }
// //       } catch (err) {
// //         console.error("Error verifying payment:", err);
// //         setError(err.response?.data?.message || "An error occurred while verifying payment");
// //       } finally {
// //         setIsSubmitting(false);
// //       }
// //     },
// //     prefill: {
// //       name: user?.name || "",
// //       email: user?.email || "",
// //       contact: user?.phone || "9999999999"
// //     },
// //     theme: {
// //       color: "#3399cc"
// //     }
// //   };

// //   const rzp = new window.Razorpay(options);
// //   rzp.open();
// // };

// const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
      
//       if (response.status === 201) {
//         toast.success("Shipping address saved successfully!");
//         setStep(2);
//       } else {
//         setError(response.data.message || "Failed to save address");
//         toast.error(response.data.message || "Failed to save address");
//       }
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while saving the address";
//       setError(errorMsg);
//       toast.error(errorMsg);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setError("");
    
//     try {
//       if (paymentMethod === "cod") {
//         await handlePlaceOrder();
//       } else {
//         await handleRazorpayPayment();
//       }
//     } catch (err) {
//       console.error("Payment error:", err);
//       const errorMsg = err.message || "An error occurred during payment processing";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   const handlePlaceOrder = async () => {
//     const token = Cookies.get("ecom_user_token");
//     try {
//       const requestBody = {
//         shippingAddress: {
//           fullName: formData.fullName,
//           addressLine1: formData.addressLine1,
//           addressLine2: formData.addressLine2,
//           city: formData.city,
//           state: formData.state,
//           zipCode: formData.zipCode,
//           country: formData.country,
//           isDefault: formData.isDefault
//         },
//         paymentMethod: {
//           type: paymentMethod
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.status === 201) {
//         setAmount(response?.data?.razorpayOrder?.amount);
//         onSubmit(response.data.order);
//         toast.success("Order placed successfully with COD!");
//         onClose();
//         clearCart();
//       } else {
//         throw new Error(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       toast.error(err.message || "Failed to place order");
//       throw err;
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     const token = Cookies.get("ecom_user_token");
//     const currency = "INR";

//     try {
//       // 1. Create order in your backend
//       const requestBody = {
//         shippingAddress: {
//           fullName: formData.fullName,
//           addressLine1: formData.addressLine1,
//           addressLine2: formData.addressLine2,
//           city: formData.city,
//           state: formData.state,
//           zipCode: formData.zipCode,
//           country: formData.country,
//           isDefault: formData.isDefault
//         },
//         paymentMethod: {
//           type: "razorpay"
//         },
//         shippingCost: orderSummary.shippingCharges
//       };

//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/create-order",
//         requestBody,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       const orderId = orderResponse?.data?.order?._id;
//       const razorpayOrderId = orderResponse?.data?.razorpayOrder?.id;
//       setAmount(orderResponse?.data?.razorpayOrder?.amount / 100); // Convert back to rupees

//       // 2. Load Razorpay script
//       await loadRazorpay();
      
//       // 3. Initialize Razorpay payment
//       const options = {
//         key: "rzp_test_heVUS0edM8228s",
//         amount: orderResponse?.data?.razorpayOrder?.amount,
//         currency,
//         name: "Uresia",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           try {
//             // Verify payment with your backend
//             const verificationResponse = await axios.post(
//               "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//               {
//                 orderId,
//                 razorpayOrderId: response.razorpay_order_id,
//                 razorpayPaymentId: response.razorpay_payment_id,
//                 razorpaySignature: response.razorpay_signature
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${token}`,
//                   "Content-Type": "application/json"
//                 }
//               }
//             );

//             if (verificationResponse?.status === 200) {
//               onSubmit(verificationResponse.data.order);
//               toast.success("Payment successful! Order placed.");
//               onClose();
//               clearCart(); 
//             } else {
//               const errorMsg = verificationResponse.data.message || "Payment verification failed";
//               setError(errorMsg);
//               toast.error(errorMsg);
//             }
//           } catch (err) {
//             console.error("Error verifying payment:", err);
//             const errorMsg = err.response?.data?.message || "An error occurred while verifying payment";
//             setError(errorMsg);
//             toast.error(errorMsg);
//           } finally {
//             setIsSubmitting(false);
//           }
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         },
//         modal: {
//           ondismiss: function() {
//             toast.info("Payment window closed");
//             setIsSubmitting(false);
//           }
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.on('payment.failed', function(response) {
//         toast.error(`Payment failed: ${response.error.description}`);
//         setIsSubmitting(false);
//       });
//       rzp.open();
//     } catch (err) {
//       console.error("Error in Razorpay payment:", err);
//       const errorMsg = err.response?.data?.message || "An error occurred while processing payment";
//       setError(errorMsg);
//       toast.error(errorMsg);
//       setIsSubmitting(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg p-6 w-full max-w-md">
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-bold">
//             {step === 1 ? "Shipping Address" : "Payment Method"}
//           </h2>
//           <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//             &times;
//           </button>
//         </div>

//         {error && (
//           <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
//             {error}
//           </div>
//         )}

//         {step === 1 ? (
//           <form onSubmit={handleAddressSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Full Name*
//                 </label>
//                 <input
//                   type="text"
//                   name="fullName"
//                   value={formData.fullName}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Address Line 1*
//                 </label>
//                 <input
//                   type="text"
//                   name="addressLine1"
//                   value={formData.addressLine1}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Address Line 2
//                 </label>
//                 <input
//                   type="text"
//                   name="addressLine2"
//                   value={formData.addressLine2}
//                   onChange={handleChange}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     City*
//                   </label>
//                   <input
//                     type="text"
//                     name="city"
//                     value={formData.city}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     State*
//                   </label>
//                   <input
//                     type="text"
//                     name="state"
//                     value={formData.state}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>
//               </div>

//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     ZIP Code*
//                   </label>
//                   <input
//                     type="text"
//                     name="zipCode"
//                     value={formData.zipCode}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Country*
//                   </label>
//                   <input
//                     type="text"
//                     name="country"
//                     value={formData.country}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>
//               </div>

//               <div className="flex items-center">
//                 <input
//                   type="checkbox"
//                   id="isDefault"
//                   name="isDefault"
//                   checked={formData.isDefault}
//                   onChange={handleChange}
//                   className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                 />
//                 <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                   Set as default shipping address
//                 </label>
//               </div>
//             </div>

//             <div className="mt-6 flex justify-end space-x-3">
//               <button
//                 type="button"
//                 onClick={onClose}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                 disabled={isSubmitting}
//               >
//                 Cancel
//               </button>
//               <button
//                 type="submit"
//                 className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "Saving..." : "Continue to Payment"}
//               </button>
//             </div>
//           </form>
//         ) : (
//           <form onSubmit={handlePaymentSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <h3 className="text-lg font-medium mb-2">Select Payment Method</h3>
//                 <div className="space-y-2">
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="cod"
//                       checked={paymentMethod === "cod"}
//                       onChange={() => setPaymentMethod("cod")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Cash on Delivery</span>
//                   </label>
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="razorpay"
//                       checked={paymentMethod === "razorpay"}
//                       onChange={() => setPaymentMethod("razorpay")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Pay with Razorpay</span>
//                   </label>
//                 </div>
//               </div>

//               <div className="pt-4 border-t border-gray-200">
//                 <h3 className="text-lg font-medium mb-2">Order Summary</h3>
//                 <div className="space-y-1 text-sm">
//                   <div className="flex justify-between">
//                     <span>Subtotal:</span>
//                     <span>{orderSummary.subtotal?.toFixed(2)} Rs</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span>Shipping:</span>
//                     <span>{orderSummary.shippingCharges?.toFixed(2)} Rs</span>
//                   </div>
//                   {/* <div className="flex justify-between">
//                     <span>Taxes:</span>
//                     <span>{orderSummary.taxes?.toFixed(2)} Rs</span>
//                   </div> */}
//                   {/* {orderSummary.appliedDiscount > 0 && (
//                     <div className="flex justify-between text-green-600">
//                       <span>Discount:</span>
//                       <span>-{orderSummary.appliedDiscount?.toFixed(2)} Rs</span>
//                     </div>
//                   )} */}
//                   <div className="flex justify-between font-medium pt-2 border-t border-gray-200">
//                     <span>Total:</span>
//                     <span>{orderSummary.total?.toFixed(2)} Rs</span>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="mt-6 flex justify-between space-x-3">
//               <button
//                 type="button"
//                 onClick={() => setStep(1)}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                 disabled={isSubmitting}
//               >
//                 Back
//               </button>
//               <button
//                 type="submit"
//                 className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "Processing..." : "Complete Order"}
//               </button>
//             </div>
//           </form>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import { useSelector } from "react-redux";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems }) => {
//   const [formData, setFormData] = useState({
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "",
//     isDefault: false
//   });
//   const [paymentMethod, setPaymentMethod] = useState("cod");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1); // 1: Address, 2: Payment

//   const { user } = useSelector(state => state.auth);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );
//       console.log("status",response.status)
//       if (response.status===201) {
//         setStep(2);
//       } else {
//         setError(response.data.message || "Failed to save address");
//       }
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       setError(err.response?.data?.message || "An error occurred while saving the address");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
    
//     if (paymentMethod === "cod") {
//       await handlePlaceOrder();
//     } else {
//       await handleRazorpayPayment();
//     }
//   };

//   const handlePlaceOrder = async () => {
//     setIsSubmitting(true);
//     try {
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/order/create",
//         {
//           shippingAddress: formData,
//           paymentMethod,
//           items: cartItems
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.data.success) {
//         onSubmit(response.data.order);
//         onClose();
//       } else {
//         setError(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       console.error("Error placing order:", err);
//       setError(err.response?.data?.message || "An error occurred while placing the order");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     setIsSubmitting(true);
//     try {
//       const token = Cookies.get("ecom_user_token");
      
//       // 1. Create order
//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/order/create",
//         {
//           shippingAddress: formData,
//           paymentMethod: "razorpay",
//           items: cartItems
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (!orderResponse.data.success) {
//         throw new Error(orderResponse.data.message || "Failed to create order");
//       }

//       const { orderId, razorpayOrderId } = orderResponse.data;

//       // 2. Load Razorpay script
//       await loadRazorpay();

//       // 3. Initialize Razorpay payment
//       const options = {
//         key: process.env.RAZORPAY_KEY_ID,
//         amount: orderSummary.total * 100,
//         currency: "INR",
//         name: "Your Store Name",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           await verifyPayment({
//             orderId,
//             razorpayOrderId: response.razorpay_order_id,
//             razorpayPaymentId: response.razorpay_payment_id,
//             razorpaySignature: response.razorpay_signature
//           });
//         },
//         prefill: {
//           name: user?.name || "",
//           email: user?.email || "",
//           contact: user?.phone || "9999999999"
//         },
//         theme: {
//           color: "#3399cc"
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.open();
      
//     } catch (err) {
//       console.error("Error processing payment:", err);
//       setError(err.message || "An error occurred during payment processing");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const verifyPayment = async (paymentData) => {
//     try {
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//         paymentData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.data.success) {
//         onSubmit(response.data.order);
//         onClose();
//       } else {
//         setError(response.data.message || "Payment verification failed");
//       }
//     } catch (err) {
//       console.error("Error verifying payment:", err);
//       setError(err.response?.data?.message || "An error occurred while verifying payment");
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg p-6 w-full max-w-md">
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-bold">
//             {step === 1 ? "Shipping Address" : "Payment Method"}
//           </h2>
//           <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//             &times;
//           </button>
//         </div>

//         {error && (
//           <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
//             {error}
//           </div>
//         )}

//         {step === 1 ? (
//           <form onSubmit={handleAddressSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Address Line 1*
//                 </label>
//                 <input
//                   type="text"
//                   name="addressLine1"
//                   value={formData.addressLine1}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Address Line 2
//                 </label>
//                 <input
//                   type="text"
//                   name="addressLine2"
//                   value={formData.addressLine2}
//                   onChange={handleChange}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     City*
//                   </label>
//                   <input
//                     type="text"
//                     name="city"
//                     value={formData.city}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     State*
//                   </label>
//                   <input
//                     type="text"
//                     name="state"
//                     value={formData.state}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>
//               </div>

//               <div className="grid grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     ZIP Code*
//                   </label>
//                   <input
//                     type="text"
//                     name="zipCode"
//                     value={formData.zipCode}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Country*
//                   </label>
//                   <input
//                     type="text"
//                     name="country"
//                     value={formData.country}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>
//               </div>

//               <div className="flex items-center">
//                 <input
//                   type="checkbox"
//                   id="isDefault"
//                   name="isDefault"
//                   checked={formData.isDefault}
//                   onChange={handleChange}
//                   className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                 />
//                 <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                   Set as default shipping address
//                 </label>
//               </div>
//             </div>

//             <div className="mt-6 flex justify-end space-x-3">
//               <button
//                 type="button"
//                 onClick={onClose}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                 disabled={isSubmitting}
//               >
//                 Cancel
//               </button>
//               <button
//                 type="submit"
//                 className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "Saving..." : "Continue to Payment"}
//               </button>
//             </div>
//           </form>
//         ) : (
//           <form onSubmit={handlePaymentSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <h3 className="text-lg font-medium mb-2">Select Payment Method</h3>
//                 <div className="space-y-2">
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="cod"
//                       checked={paymentMethod === "cod"}
//                       onChange={() => setPaymentMethod("cod")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Cash on Delivery</span>
//                   </label>
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="razorpay"
//                       checked={paymentMethod === "razorpay"}
//                       onChange={() => setPaymentMethod("razorpay")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Pay with Razorpay</span>
//                   </label>
//                 </div>
//               </div>

//               <div className="pt-4 border-t border-gray-200">
//                 <h3 className="text-lg font-medium mb-2">Order Summary</h3>
//                 <div className="space-y-1 text-sm">
//                   <div className="flex justify-between">
//                     <span>Subtotal:</span>
//                     <span>{orderSummary.subtotal?.toFixed(2)} Rs</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span>Shipping:</span>
//                     <span>{orderSummary.shippingCharges?.toFixed(2)} Rs</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span>Taxes:</span>
//                     <span>{orderSummary.taxes?.toFixed(2)} Rs</span>
//                   </div>
//                   {orderSummary.appliedDiscount > 0 && (
//                     <div className="flex justify-between text-green-600">
//                       <span>Discount:</span>
//                       <span>-{orderSummary.appliedDiscount?.toFixed(2)} Rs</span>
//                     </div>
//                   )}
//                   <div className="flex justify-between font-medium pt-2 border-t border-gray-200">
//                     <span>Total:</span>
//                     <span>{orderSummary.total?.toFixed(2)} Rs</span>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="mt-6 flex justify-between space-x-3">
//               <button
//                 type="button"
//                 onClick={() => setStep(1)}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                 disabled={isSubmitting}
//               >
//                 Back
//               </button>
//               <button
//                 type="submit"
//                 className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "Processing..." : "Complete Order"}
//               </button>
//             </div>
//           </form>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { loadRazorpay } from "../../../Utils/razorpay";
// import {useSelector } from "react-redux";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary, cartItems }) => {
//   const [formData, setFormData] = useState({
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "",
//     isDefault: false
//   });
//     const { user } = useSelector(state => state.auth);

//   const [paymentMethod, setPaymentMethod] = useState("cod"); // 'cod' or 'razorpay'
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [error, setError] = useState("");
//   const [step, setStep] = useState(1); // 1: Address, 2: Payment

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleAddressSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.data.success) {
//         setStep(2); // Move to payment step
//       } else {
//         setError(response.data.message || "Failed to save address");
//       }
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       setError(err.response?.data?.message || "An error occurred while saving the address");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handlePaymentSubmit = async (e) => {
//     e.preventDefault();
    
//     if (paymentMethod === "cod") {
//       // Handle Cash on Delivery
//       await handlePlaceOrder();
//     } else {
//       // Handle Razorpay payment
//       await handleRazorpayPayment();
//     }
//   };

//   const handlePlaceOrder = async () => {
//     setIsSubmitting(true);
//     try {
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/order/create",
//         {
//           shippingAddress: formData,
//           paymentMethod,
//           items: cartItems
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.data.success) {
//         onSubmit(response.data.order); // Notify parent component
//         onClose(); // Close the modal
//       } else {
//         setError(response.data.message || "Failed to place order");
//       }
//     } catch (err) {
//       console.error("Error placing order:", err);
//       setError(err.response?.data?.message || "An error occurred while placing the order");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleRazorpayPayment = async () => {
//     setIsSubmitting(true);
//     try {
//       const token = Cookies.get("ecom_user_token");
      
//       // 1. First create an order on your backend
//       const orderResponse = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/order/create",
//         {
//           shippingAddress: formData,
//           paymentMethod: "razorpay",
//           items: cartItems
//         },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (!orderResponse.data.success) {
//         throw new Error(orderResponse.data.message || "Failed to create order");
//       }

//       const { orderId, razorpayOrderId } = orderResponse.data;

//       // 2. Load Razorpay script
//       await loadRazorpay();

//       // 3. Initialize Razorpay payment
//       const options = {
//         key: process.env.RAZORPAY_KEY_ID, // Your Razorpay key
//         amount: orderSummary.total * 100, // Amount in paise
//         currency: "INR",
//         name: "Your Store Name",
//         description: "Order Payment",
//         order_id: razorpayOrderId,
//         handler: async function(response) {
//           // Handle successful payment
//           await verifyPayment({
//             orderId,
//             razorpayOrderId: response.razorpay_order_id,
//             razorpayPaymentId: response.razorpay_payment_id,
//             razorpaySignature: response.razorpay_signature
//           });
//         },
//         prefill: {
//           name: user?.name,
//           email: user?.email,
//           contact: user?.phone 
//         },
//         theme: {
//           color: "#3399cc"
//         }
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.open();
      
//     } catch (err) {
//       console.error("Error processing payment:", err);
//       setError(err.message || "An error occurred during payment processing");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const verifyPayment = async (paymentData) => {
//     try {
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/payment/verify-payment",
//         paymentData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.data.success) {
//         onSubmit(response.data.order); // Notify parent component
//         onClose(); // Close the modal
//       } else {
//         setError(response.data.message || "Payment verification failed");
//       }
//     } catch (err) {
//       console.error("Error verifying payment:", err);
//       setError(err.response?.data?.message || "An error occurred while verifying payment");
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg p-6 w-full max-w-md">
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-bold">
//             {step === 1 ? "Shipping Address" : "Payment Method"}
//           </h2>
//           <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//             &times;
//           </button>
//         </div>

//         {error && (
//           <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
//             {error}
//           </div>
//         )}

//         {step === 1 ? (
//           <form onSubmit={handleAddressSubmit}>
//             {/* Address form fields (same as before) */}
//             {/* ... */}

//             <div className="mt-6 flex justify-end space-x-3">
//               <button
//                 type="button"
//                 onClick={onClose}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                 disabled={isSubmitting}
//               >
//                 Cancel
//               </button>
//               <button
//                 type="submit"
//                 className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "Saving..." : "Continue to Payment"}
//               </button>
//             </div>
//           </form>
//         ) : (
//           <form onSubmit={handlePaymentSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <h3 className="text-lg font-medium mb-2">Select Payment Method</h3>
//                 <div className="space-y-2">
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="cod"
//                       checked={paymentMethod === "cod"}
//                       onChange={() => setPaymentMethod("cod")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Cash on Delivery</span>
//                   </label>
//                   <label className="flex items-center space-x-2 p-3 border rounded-md hover:border-blue-500">
//                     <input
//                       type="radio"
//                       name="paymentMethod"
//                       value="razorpay"
//                       checked={paymentMethod === "razorpay"}
//                       onChange={() => setPaymentMethod("razorpay")}
//                       className="h-4 w-4 text-blue-600 focus:ring-blue-500"
//                     />
//                     <span>Pay with Razorpay</span>
//                   </label>
//                 </div>
//               </div>

//               {/* Order Summary */}
//               <div className="pt-4 border-t border-gray-200">
//                 <h3 className="text-lg font-medium mb-2">Order Summary</h3>
//                 <div className="space-y-1 text-sm">
//                   {/* Order summary items (same as before) */}
//                   {/* ... */}
//                 </div>
//               </div>
//             </div>

//             <div className="mt-6 flex justify-between space-x-3">
//               <button
//                 type="button"
//                 onClick={() => setStep(1)}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//                 disabled={isSubmitting}
//               >
//                 Back
//               </button>
//               <button
//                 type="submit"
//                 className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "Processing..." : "Complete Order"}
//               </button>
//             </div>
//           </form>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;

// import React, { useState } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary }) => {
//   const [formData, setFormData] = useState({
//     addressLine1: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zipCode: "",
//     country: "",
//     isDefault: false
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [error, setError] = useState("");

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setIsSubmitting(true);

//     const token = Cookies.get("ecom_user_token");
//     try {
//       const response = await axios.post(
//         "https://seller-backend-29o9.onrender.com/api/buyer/add-shipping-address",
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json"
//           }
//         }
//       );

//       if (response.data.success) {
//         onSubmit(formData); // Pass the form data back to parent
//         onClose(); // Close the modal
//       } else {
//         setError(response.data.message || "Failed to save address");
//       }
//     } catch (err) {
//       console.error("Error saving shipping address:", err);
//       setError(err.response?.data?.message || "An error occurred while saving the address");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg p-6 w-full max-w-md">
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-bold">Shipping Address</h2>
//           <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//             &times;
//           </button>
//         </div>

//         {error && (
//           <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
//             {error}
//           </div>
//         )}

//         <form onSubmit={handleSubmit}>
//           <div className="space-y-4">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Address Line 1*
//               </label>
//               <input
//                 type="text"
//                 name="addressLine1"
//                 value={formData.addressLine1}
//                 onChange={handleChange}
//                 required
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Address Line 2
//               </label>
//               <input
//                 type="text"
//                 name="addressLine2"
//                 value={formData.addressLine2}
//                 onChange={handleChange}
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//               />
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   City*
//                 </label>
//                 <input
//                   type="text"
//                   name="city"
//                   value={formData.city}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   State*
//                 </label>
//                 <input
//                   type="text"
//                   name="state"
//                   value={formData.state}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   ZIP Code*
//                 </label>
//                 <input
//                   type="text"
//                   name="zipCode"
//                   value={formData.zipCode}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Country*
//                 </label>
//                 <input
//                   type="text"
//                   name="country"
//                   value={formData.country}
//                   onChange={handleChange}
//                   required
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>
//             </div>

//             <div className="flex items-center">
//               <input
//                 type="checkbox"
//                 id="isDefault"
//                 name="isDefault"
//                 checked={formData.isDefault}
//                 onChange={handleChange}
//                 className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//               />
//               <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
//                 Set as default shipping address
//               </label>
//             </div>
//           </div>

//           {/* Order Summary */}
//           <div className="mt-6 pt-4 border-t border-gray-200">
//             <h3 className="text-lg font-medium mb-2">Order Summary</h3>
//             <div className="space-y-1 text-sm">
//               <div className="flex justify-between">
//                 <span>Subtotal:</span>
//                 <span>{orderSummary.subtotal?.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between">
//                 <span>Shipping:</span>
//                 <span>{orderSummary.shippingCharges?.toFixed(2)} Rs</span>
//               </div>
//               <div className="flex justify-between">
//                 <span>Taxes:</span>
//                 <span>{orderSummary.taxes?.toFixed(2)} Rs</span>
//               </div>
//               {orderSummary.appliedDiscount > 0 && (
//                 <div className="flex justify-between text-green-600">
//                   <span>Discount:</span>
//                   <span>-{orderSummary.appliedDiscount?.toFixed(2)} Rs</span>
//                 </div>
//               )}
//               <div className="flex justify-between font-medium pt-2 border-t border-gray-200">
//                 <span>Total:</span>
//                 <span>{orderSummary.total?.toFixed(2)} Rs</span>
//               </div>
//             </div>
//           </div>

//           <div className="mt-6 flex justify-end space-x-3">
//             <button
//               type="button"
//               onClick={onClose}
//               className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//               disabled={isSubmitting}
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
//               disabled={isSubmitting}
//             >
//               {isSubmitting ? "Saving..." : "Save Address & Continue"}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default ShippingAddress;
// import React, { useState } from "react";
// import PaymentGateway from "./PaymentGateway";

// const ShippingAddress = ({ onClose, onSubmit, orderSummary }) => {
//   const [address, setAddress] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     street: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zip: "",
//     country: "",
//     deliveryDate: "",
//     termsAccepted: false,
//   });

//   const [showPayment, setShowPayment] = useState(false);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setAddress((prevAddress) => ({
//       ...prevAddress,
//       [name]: type === "checkbox" ? checked : value,
//     }));
//   };

//   const isAddressComplete = () => {
//     const requiredFields = [
//       "name",
//       "email",
//       "phone",
//       "street",
//       "city",
//       "state",
//       "zip",
//       "country",
//       "deliveryDate",
//       "termsAccepted",
//     ];
//     return requiredFields.every((field) => {
//       if (field === "termsAccepted") {
//         return address[field] === true;
//       }
//       return address[field] && address[field].trim() !== "";
//     });
//   };

//   const handleContinueToPayment = () => {
//     if (isAddressComplete()) {
//       setShowPayment(true);
//     } else {
//       alert(
//         "Please fill out all required fields and accept the terms and conditions."
//       );
//     }
//   };

//   return (
//     <>
//       {/* Modal Overlay */}
//       <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
//         {/* Modal Container */}
//         <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
//           {/* Modal Header */}
//           <div className="flex justify-between items-center p-4 border-b">
//             <h2 className="text-xl font-semibold text-gray-800">Shipping Address</h2>
//             <button
//               onClick={onClose}
//               className="text-gray-500 hover:text-gray-700"
//             >
//               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//               </svg>
//             </button>
//           </div>
          
//           {/* Modal Content */}
//           <div className="p-6">
//             <div className="flex flex-col lg:flex-row gap-6">
//               {/* Address Form */}
//               <div className="lg:w-2/3">
//                 <form className="space-y-4">
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     {[
//                       { label: "Full Name", name: "name", type: "text", required: true },
//                       { label: "Email", name: "email", type: "email", required: true },
//                       { label: "Phone Number", name: "phone", type: "tel", required: true },
//                       { label: "Street Address", name: "street", type: "text", required: true },
//                       { label: "Address Line 2", name: "addressLine2", type: "text", required: false },
//                       { label: "City", name: "city", type: "text", required: true },
//                       { label: "State", name: "state", type: "text", required: true },
//                       { label: "Zip Code", name: "zip", type: "text", required: true },
//                       { label: "Country", name: "country", type: "text", required: true },
//                     ].map(({ label, name, type, required }) => (
//                       <div key={name} className="space-y-1">
//                         <label htmlFor={name} className="block text-sm font-medium text-gray-700">
//                           {label} {required && <span className="text-red-500">*</span>}
//                         </label>
//                         <input
//                           type={type}
//                           id={name}
//                           name={name}
//                           value={address[name]}
//                           onChange={handleChange}
//                           required={required}
//                           className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                         />
//                       </div>
//                     ))}
//                   </div>

//                   <div className="space-y-1">
//                     <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700">
//                       Delivery Date <span className="text-red-500">*</span>
//                     </label>
//                     <input
//                       type="date"
//                       id="deliveryDate"
//                       name="deliveryDate"
//                       value={address.deliveryDate}
//                       onChange={handleChange}
//                       required
//                       className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                     />
//                   </div>

//                   <div className="flex items-start">
//                     <div className="flex items-center h-5">
//                       <input
//                         id="termsAccepted"
//                         name="termsAccepted"
//                         type="checkbox"
//                         checked={address.termsAccepted}
//                         onChange={handleChange}
//                         required
//                         className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
//                       />
//                     </div>
//                     <div className="ml-3 text-sm">
//                       <label htmlFor="termsAccepted" className="font-medium text-gray-700">
//                         I accept the terms and conditions <span className="text-red-500">*</span>
//                       </label>
//                     </div>
//                   </div>
//                 </form>
//               </div>

//               {/* Order Summary */}
//               <div className="lg:w-1/3 bg-gray-50 p-4 rounded-lg">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Summary</h3>
//                 <div className="space-y-3">
//                   {[
//                     { label: "Subtotal", value: orderSummary.subtotal },
//                     { label: "Shipping Charges", value: orderSummary.shippingCharges },
//                     { label: "Taxes (5%)", value: orderSummary.taxes },
//                   ].map(({ label, value }) => (
//                     <div key={label} className="flex justify-between text-sm text-gray-600">
//                       <span>{label}</span>
//                       <span>{value.toFixed(2)} Rs</span>
//                     </div>
//                   ))}
//                   {orderSummary.appliedDiscount > 0 && (
//                     <div className="flex justify-between text-sm text-green-600">
//                       <span>Discount</span>
//                       <span>-{orderSummary.appliedDiscount.toFixed(2)} Rs</span>
//                     </div>
//                   )}
//                   <div className="border-t border-gray-200 pt-3 mt-2">
//                     <div className="flex justify-between font-semibold">
//                       <span>Total</span>
//                       <span>{orderSummary.total.toFixed(2)} Rs</span>
//                     </div>
//                   </div>
//                 </div>
//                 <button
//                   onClick={handleContinueToPayment}
//                   disabled={!isAddressComplete()}
//                   className={`w-full mt-6 py-2 px-4 rounded-md text-white font-medium ${
//                     isAddressComplete()
//                       ? "bg-blue-600 hover:bg-blue-700"
//                       : "bg-gray-400 cursor-not-allowed"
//                   } transition duration-200`}
//                 >
//                   Continue to Payment
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {showPayment && (
//         <PaymentGateway
//           onClose={() => setShowPayment(false)}
//           orderSummary={orderSummary}
//           shippingAddress={address}
//         />
//       )}
//     </>
//   );
// };

// export default ShippingAddress;

// import React, { useState } from "react";
// import PaymentGateway from "./PaymentGateway"; // Import the Payment Gateway component

// const ShippingAddress = ({ onClose, onSubmit, orderSummary }) => {
//   const [address, setAddress] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     street: "",
//     addressLine2: "",
//     city: "",
//     state: "",
//     zip: "",
//     country: "",
//     deliveryDate: "",
//     termsAccepted: false,
//   });

//   const [showPayment, setShowPayment] = useState(false);

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setAddress((prevAddress) => ({
//       ...prevAddress,
//       [name]: type === "checkbox" ? checked : value,
//     }));
//   };

//   const isAddressComplete = () => {
//     const requiredFields = [
//       "name",
//       "email",
//       "phone",
//       "street",
//       "city",
//       "state",
//       "zip",
//       "country",
//       "deliveryDate",
//       "termsAccepted",
//     ];
//     return requiredFields.every((field) => address[field]);
//   };

//   const handleContinueToPayment = () => {
//     if (isAddressComplete()) {
//       setShowPayment(true);
//     } else {
//       alert(
//         "Please fill out all required fields and accept the terms and conditions."
//       );
//     }
//   };

//   return (
//     <div>
//       <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
//         <div className="bg-white p-6 rounded-lg shadow-2xl w-full lg:w-11/12 h-[90%] lg:h-9/10 flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-6 overflow-hidden">
//           <div className="w-full lg:w-2/3">
//             <h2 className="text-lg font-semibold text-gray-800 mb-6">
//               Shipping Address
//             </h2>
//             <form className="space-y-4">
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                 {[
//                   { label: "Full Name", name: "name" },
//                   { label: "Email", name: "email" },
//                   { label: "Phone Number", name: "phone" },
//                   { label: "Street Address", name: "street" },
//                   { label: "City", name: "city" },
//                   { label: "State", name: "state" },
//                   { label: "Zip Code", name: "zip" },
//                   { label: "Country", name: "country" },
//                 ].map(({ label, name }) => (
//                   <div key={name} className="flex flex-col space-y-2">
//                     <label
//                       htmlFor={name}
//                       className="text-sm font-medium text-gray-600"
//                     >
//                       {label}
//                     </label>
//                     <input
//                       type="text"
//                       name={name}
//                       id={name}
//                       value={address[name]}
//                       onChange={handleChange}
//                       className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
//                       required={name !== "addressLine2"}
//                     />
//                   </div>
//                 ))}
//               </div>

//               <div className="flex flex-col space-y-2">
//                 <label
//                   htmlFor="deliveryDate"
//                   className="text-sm font-medium text-gray-600"
//                 >
//                   Delivery Date
//                 </label>
//                 <input
//                   type="date"
//                   name="deliveryDate"
//                   id="deliveryDate"
//                   value={address.deliveryDate}
//                   onChange={handleChange}
//                   className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
//                 />
//               </div>

//               <div className="flex items-center space-x-2 mb-4">
//                 <input
//                   type="checkbox"
//                   name="termsAccepted"
//                   checked={address.termsAccepted}
//                   onChange={handleChange}
//                   className="mr-2"
//                   required
//                 />
//                 <label
//                   htmlFor="termsAccepted"
//                   className="text-sm text-gray-700"
//                 >
//                   I accept the terms and conditions
//                 </label>
//               </div>
//             </form>
//           </div>

//           <div className="w-full lg:w-1/3 h-[60%] bg-gray-100 rounded-lg p-5 shadow-md">
//             <h3 className="text-lg font-semibold text-gray-800 mb-4">
//               Order Summary
//             </h3>
//             <div className="space-y-4">
//               <div className="flex justify-between text-sm text-gray-600">
//                 <span>Total Items</span>
//                 <span>{orderSummary.totalItems}</span>
//               </div>
//               {[
//                 { label: "Subtotal", value: orderSummary.subtotal },
//                 {
//                   label: "Shipping Charges",
//                   value: orderSummary.shippingCharges,
//                 },
//                 { label: "Taxes (5%)", value: orderSummary.taxes },
//               ].map(({ label, value }) => (
//                 <div
//                   key={label}
//                   className="flex justify-between text-sm text-gray-600"
//                 >
//                   <span>{label}</span>
//                   <span>{value.toFixed(2)} Rs</span>
//                 </div>
//               ))}
//               {orderSummary.appliedDiscount > 0 && (
//                 <div className="flex justify-between text-sm text-green-600">
//                   <span>Discount</span>
//                   <span>-{orderSummary.appliedDiscount.toFixed(2)} Rs</span>
//                 </div>
//               )}
//               <div className="flex justify-between font-semibold text-lg pt-2 border-t border-gray-300">
//                 <span>Total</span>
//                 <span>{orderSummary.total.toFixed(2)} Rs</span>
//               </div>
//             </div>
//             <button
//               className="w-full bg-blue-600 text-white py-2 px-4 text-sm rounded-md hover:bg-blue-700 transition duration-200 mt-4"
//               onClick={handleContinueToPayment}
//             >
//               Continue to Payment
//             </button>
//           </div>
//         </div>
//       </div>

//       {showPayment && (
//         <PaymentGateway
//           onClose={() => setShowPayment(false)}
//           orderSummary={orderSummary}
//         />
//       )}
//     </div>
//   );
// };

// export default ShippingAddress;
